import {
  BadRequestError, BookingError, BookingSourceType, BookingState,
  CanCheckIn, ErrorCode, Gym, GymTypes, HandledError, IEnabledFeatures,
  IInvitationDetails, ILiveStreamOptions, KnownMediums, NotFoundError, Organisation, ProgramType,
  RemovedReasonType, TimeSlotEligibility,
  TimeSlotType, TimeSpan, TimeZoneMapping, WorkoutType,
} from 'fitbeat.models';
import { INotificationService, INotifyResults } from 'fitbeat.notification-service';
import moment from 'moment-timezone';
import {
  AreaCategory,
  getAllGymAreaCategoriesInPriorityOrder,
  getStaticZoneNumberFromArea,
  GymConfigs,
  isStaticCardioZone,
  ProgramsConfig,
} from '../../config';
import { BookingsConfig } from '../../config/bookings';
import {
  getAmountOfStationsForCategory, getAreaCategory, isStaticFunctionalZone, staticFunctionalAreaZones,
} from '../../config/zone-mappings';
import { GymInfoForTimeslots } from '../../entities/gym/gyminforfortimeslots';
import { IGymSchedule } from '../../entities/gymschedule';
import {
  DisplayableUserProfile, IDisplayableUserProfile, UserProfile, WorkoutSettings,
} from '../../entities/user';
import { WaitlistEntry } from '../../entities/waitlist';
import {
  IExternalReference,
  IInfluencerBookings,
  IStartTimeGroupedSessions,
  IUserWorkoutSessionProfile,
  UserWorkoutSession,
  UserWorkoutSessionWithDisplayInfo,
} from '../../entities/workoutsession';
import { IDisplayableOpenInviteDetails } from '../../entities/workoutsession/openinvites';
import { setBookingsRemovedState, UserTimeSlotTypeEligibility } from '../../helpers';
import { DbGymRepository, WorkoutFilter } from '../../persistence';
import { DemeritsManager } from '../demerits';
import {
  IBaseTimeSlot, IBaseTimeSlotWithAvailability,
  IGymScheduler,
  ITimeSlot, ITimeslotCapacity, TimeSlotClosedReason,
} from '../gymscheduling/igymscheduling';
import { MarketingAnalyticsService } from '../marketing-analytics';
import { PartnersManager } from '../partners';
import { Waitlist } from '../waitlist/waitlist';
import { WorkoutProgramRecommendations } from '../workoutprograms';
import { GymTimeSlotsManager } from './gymtimeslotsmanager';
import { HomeBookingsManager } from './homebookingsmanager';
import { HomeTimeSlot } from './hometimeslot';
import {
  IBookingRequest, IBookingsManager, ICalculateTimeSlotsOptions, IUserSessionHistoryEntry,
} from './ibookingsmanager';
import {
  determineAccordionCondition,
  determineStatus,
  determineWarningText,
  IAvailableStudioTimeSlots,
  INoOnDemandSlotAvailable,
  IOnDemandSlotAvailable,
  ISelectStudioClassTimeData,
  IWorkoutTypeClassTimeData,
  OnDemandBookingSlotData,
  UnavailableReason,
} from './select-studio-class-time';
import { ITimeSlotDisplayContent, ITimeSlotViewModel } from './time-slot-view-model';
import { ITimeSlotWithEligibility } from './time-slot-with-eligibility';

type TimeSlotTypeOrCancelled = TimeSlotType | 'Cancelled' | undefined;

interface ITimeSlotTypeRange {
  timeTypeAtStartOfSlot: TimeSlotTypeOrCancelled;
  timeTypeAtEndOfSlot: TimeSlotTypeOrCancelled;
}

export const errorMessage = {
  demerittedNotAllowedToBook: 'Sorry, you cannot book on this day.'
    + ' To give other users preference you can now only book 5 days ahead'
    + ' since you have missed two classes in the last week',
  checkInTooEarly: 'Can only check in a user within an hour of their session',
};

export class BookingsManager implements IBookingsManager {
  public static groupBookingsByDay = (
    bookings: UserWorkoutSession[],
    timeZone: string,
  ): UserWorkoutSession[][] => {
    if (bookings.length === 0) {
      return [];
    }
    const sortedBookings = bookings.sort((a, b) => (
      moment(a.startTimeOfFirstExercise).unix()
      - moment(b.startTimeOfFirstExercise).unix()
    ));
    const groupedByDay = sortedBookings.reduce(
      (
        acc: { [key: string]: UserWorkoutSession[] },
        cur: UserWorkoutSession,
      ) => {
        const timeOfFirstExercise = moment(cur.startTimeOfFirstExercise).tz(timeZone);
        const dayKey = `${timeOfFirstExercise.format('YYYY-MM-DD')} ${timeZone}`;
        if (acc[dayKey]) {
          acc[dayKey].push(cur);
          return acc;
        }
        acc[dayKey] = [cur];
        return acc;
      },
      {},
    );
    return Object.values(groupedByDay);
  };

  private static mergeOnDemandSlotsIntoClassTimeArrays(
    onDemand: IOnDemandSlotAvailable,
    availableTimeSlots: IWorkoutTypeClassTimeData[],
  ) {
    for (const onDemandSlot of onDemand.slots) {
      const workoutTypeGroup = availableTimeSlots.find((x) => x.workoutType === onDemandSlot.workoutType);
      const mappedTimeSlot: ITimeSlotViewModel = {
        ...onDemandSlot,
        eligibility: TimeSlotEligibility.Eligible,
        subText: 'nextAvailable',
      };

      if (workoutTypeGroup && workoutTypeGroup.classTimes.length > 0) {
        const onDemandStartTime = mappedTimeSlot.startTime;
        const firstClassTimeStartTime = workoutTypeGroup.classTimes[0].startTime;

        if (onDemandStartTime < firstClassTimeStartTime) {
          workoutTypeGroup.classTimes = [mappedTimeSlot, ...workoutTypeGroup.classTimes];
        } else if (onDemandStartTime > firstClassTimeStartTime) {
          // replace class time that starts before this on demand slot with the on demand slot
          workoutTypeGroup.classTimes[0] = mappedTimeSlot;
        } else {
          // on demand slot overlaps the class time: use the class time
        }
      } else {
        availableTimeSlots.push({
          classTimes: [mappedTimeSlot],
          workoutType: onDemandSlot.workoutType,
        });
      }
    }
  }

  private static getAllowedWorkoutTypesForOnDemand(gym: GymInfoForTimeslots, areaCategory: AreaCategory): number[] {
    if (isStaticCardioZone(areaCategory)) {
      // never
      return [];
    }

    if (isStaticFunctionalZone(areaCategory)) {
      const staticFunctional = BookingsConfig
        .getStaticFunctionalBookingTimeFrames()
        .filter((timeFrame) => gym.allowedBookingTimeFrames.isAllowedForStaticFunctionalOnDemand(timeFrame));
      return staticFunctional.map((timeFrame) => timeFrame.workoutType);
    }

    const circuit = BookingsConfig
      .getCircuitBookingTimeFrames()
      .filter((timeFrame) => gym.allowedBookingTimeFrames.isAllowedForCircuitOnDemand(timeFrame));
    return circuit.map((timeFrame) => timeFrame.workoutType);
  }

  private waitlist: Waitlist | undefined;

  private demeritsManager: DemeritsManager;

  private partnersManager: PartnersManager;

  private asyncQueue = Array<Promise<boolean>>();

  private gymTimeSlotsManager: GymTimeSlotsManager;

  private homeBookingManager: HomeBookingsManager;

  constructor(
    private gymScheduler: IGymScheduler,
    private logger: any,
    private notificationService?: INotificationService,
    private marketingAnalyticsService?: MarketingAnalyticsService,
  ) {
    const userRepo = this.gymScheduler.getUserProfileRepo();
    this.demeritsManager = new DemeritsManager(userRepo);
    this.partnersManager = new PartnersManager(userRepo);
    // todo: move into static initializeInBackground, or change services to initialize repo once
    this.gymTimeSlotsManager = GymTimeSlotsManager.initializeInBackground(this.gymScheduler.getGymTimeSlotsRepo());
    this.homeBookingManager = new HomeBookingsManager(
      this.gymScheduler.workoutRepository,
      userRepo,
      this.partnersManager,
      this.logger,
    );
  }

  public getGymTimeSlotsManager(): GymTimeSlotsManager {
    return this.gymTimeSlotsManager;
  }

  public async waitForAsyncTasksToComplete() {
    // test helper to wait for fire-and-forget async tasks to complete
    while (this.asyncQueue.length > 0) {
      const p = this.asyncQueue.shift();
      await p;
    }
  }

  public setWaitList(waitlist: Waitlist) {
    this.waitlist = waitlist;
  }

  public async GetCapacity(
    from = new Date(),
    to?: Date,
    dateOfRequest = new Date(),
    gym: Gym = DbGymRepository.getTestGym(),
  ): Promise<ITimeslotCapacity[]> {
    if (!to) {
      to = TimeSpan.createEndOfDayDate(from);
    }
    const roundedTime = this.roundToHour(from);
    return this.calculateCapacity(
      roundedTime,
      to,
      await this.gymScheduler.gymScheduleAt(from, to, gym, dateOfRequest),
      DbGymRepository.getGymInfoForTimeslots(gym),
    );
  }

  public groupWorkoutSessionsByStartTime(
    collection: UserWorkoutSessionWithDisplayInfo[],
  ): IStartTimeGroupedSessions[] {
    let index;
    const values = [];
    const result: IStartTimeGroupedSessions[] = [];

    for (const iterator of collection) {
      const val = iterator.startTimeOfFirstExercise;
      index = values.findIndex((date) => date.getTime() === val.getTime());
      if (index > -1) {
        result[index].members.push(iterator);
      } else {
        values.push(val);
        const timezone = moment.tz.guess();
        const isClassTimeToday = moment().isSame(moment(val), 'day');
        const classTime = isClassTimeToday
          ? `Today ${moment.tz(val, timezone).format('D MMM HH:mm')}`
          : moment.tz(val, timezone).format('dddd D MMM HH:mm');
        result.push({ classTime, members: [iterator] });
      }
    }
    return result;
  }

  public async GetAvailableBookingSlotsForInfluencer(
    userId: string,
  ): Promise<IInfluencerBookings> {
    const influencerProfile = await this.gymScheduler.getUserProfile(userId);
    if (influencerProfile && influencerProfile.influencerSettings) {
      if (!influencerProfile.gymName) {
        throw new Error(`no gym found for user : ${influencerProfile.gymName}`);
      }
      const gym = DbGymRepository.getByName(influencerProfile.gymName);
      const influencerBookings = await this.homeBookingManager.GetAvailableInfluencerBookingSlots(userId);
      const mappedInfluencerBookings: IInfluencerBookings = {
        userProfileId: influencerProfile.uid,
        firstname: influencerProfile.firstname,
        landingPage: gym.defaultHomeSignupFlow ? undefined : gym.landingPage,
        personalisedHeader: gym.personalisedHeader,
        influencerBookings,
      };
      return mappedInfluencerBookings;
    }
    throw new Error('User not an influencer');
  }

  public async GetAvailableBookingSlotsForHome(
    gym: Gym,
    userId: string,
    from: Date,
    dateOfRequest: Date,
    timezone: string,
  ): Promise<HomeTimeSlot[]> {
    let homeTimeSlot: HomeTimeSlot[] = [];
    if (gym.type === GymTypes.Home) {
      homeTimeSlot = await this.homeBookingManager.GetAvailableBookingSlots(
        userId,
        from,
        dateOfRequest,
        timezone,
      );
    }

    return homeTimeSlot;
  }

  public async classTimesAndOnDemandSlotsForUserInGym(
    userId: string,
    gymName: string,
    yyyymmdd: string,
    withPartner: boolean,
    requestTime: Date,
    staticZoneType: string = 'circuit',
    workoutSessions?: UserWorkoutSession[],
    schedule?: IGymSchedule,
    includeOnDemandStaticFunctionalBookings = false,
  ): Promise<ISelectStudioClassTimeData> {
    const userProfile = await this.gymScheduler.getUserProfileRepo().getUserProfileOrThrow(userId);
    const gym = DbGymRepository.getByName(gymName);
    const fromDate = TimeZoneMapping.dateInTimezoneToUTCDate(yyyymmdd, gym.timezone);
    const toDate = TimeZoneMapping.getEndOfDayInTimezone(yyyymmdd, gym.timezone);
    const staticZoneNumber = getStaticZoneNumberFromArea(staticZoneType);

    const classTimes = await this.GetAvailableBookingSlotsForGym(
      withPartner,
      gym,
      userProfile,
      fromDate,
      toDate,
      requestTime,
      staticZoneNumber,
      workoutSessions,
      schedule,
    );
    const userHasExistingBookingForDay = !!classTimes.find((classTime) => classTime.booked);
    const canBookOnDemandInThisArea = () => {
      if (staticZoneType === 'circuit' || !staticZoneType) {
        // explicit or implicitly circuit
        return true;
      }
      if (includeOnDemandStaticFunctionalBookings) {
        return isStaticFunctionalZone(staticZoneType as AreaCategory);
      }
      return false;
    };

    const onDemand = canBookOnDemandInThisArea()
      ? await this.getOnDemandBookingSlotData(
        gym,
        userProfile,
        requestTime,
        fromDate,
        withPartner,
        userHasExistingBookingForDay,
        staticZoneType as AreaCategory,
      )
      : { unavailableReason: UnavailableReason.notAvailableForStaticZones } as INoOnDemandSlotAvailable;
    return {
      classTimes,
      onDemand,
    };
  }

  public async getTimeSlotsForUserInGym(
    userId: string,
    gymName: string,
    yyyymmdd: string,
    withPartner: boolean,
    requestTime: Date,
    /*
    * added for compatibility in tests
    * a lot of tests create a booking and then loads the availability to check if the booking was made
    */
    returnSlotsIfUserHasBookingOnDay = false,
    useDeprecatedGoalNotAlignedError = false,
  ): Promise<IAvailableStudioTimeSlots[]> {
    const dataTimer = this.logger.startTimer();
    const userProfile = await this.gymScheduler.getUserProfileRepo().getUserProfileOrThrow(userId);
    const gym = DbGymRepository.getByName(gymName);
    const gymZoneCategories = getAllGymAreaCategoriesInPriorityOrder(gym.zoneMapping);
    const availableStudioSlots: IAvailableStudioTimeSlots[] = [];
    const fromDate = TimeZoneMapping.dateInTimezoneToUTCDate(yyyymmdd, gym.timezone);
    const fromDateToUse = this.calculateDateToUseForSlots(fromDate, requestTime, gym);
    const sessions = await this.gymScheduler.getConfirmedBookingsForUser(
      userProfile.uid,
      fromDateToUse,
      undefined,
      requestTime,
    );
    const toDate = TimeZoneMapping.getEndOfDayInTimezone(yyyymmdd, gym.timezone);
    const schedule = await this.gymScheduler.gymScheduleAt(fromDateToUse, toDate, gym, requestTime);
    dataTimer.done(`Fetching data from DB's for user (${userId})`);

    const defaultSettings = ProgramsConfig.fallbackSettingsForGoal(userProfile.longTermGoal, userProfile.skillLevel);

    const startOfToday = TimeSpan.createStartOfDayDateInTimezone(fromDateToUse, gym.timezone);
    const userBookingsOnThisDay = sessions
      .filter((s: UserWorkoutSession) => s.endTime > requestTime)
      .filter((s: UserWorkoutSession) => s.startTime >= startOfToday && s.endTime <= toDate);
    const userHasABookingOnTheSameDayAsRequested = userBookingsOnThisDay.length > 0;
    if (userHasABookingOnTheSameDayAsRequested && !returnSlotsIfUserHasBookingOnDay) {
      for (const areaCategory of gymZoneCategories) {
        availableStudioSlots.push({
          availableTimeSlots: [],
          areaCategory,
          status: 'Not Available',
          accordionCondition: 'Disabled',
          warning: determineWarningText(
            areaCategory,
            withPartner,
            defaultSettings.cardioOrResistance,
            userHasABookingOnTheSameDayAsRequested,
            useDeprecatedGoalNotAlignedError,
          ),
        });
      }

      return availableStudioSlots;
    }

    for (const zoneCategory of gymZoneCategories) {
      const timer = this.logger.startTimer();
      const includeOnDemandStaticFunctionalBookings = true;
      const { classTimes, onDemand } = await this.classTimesAndOnDemandSlotsForUserInGym(
        userId,
        gymName,
        yyyymmdd,
        withPartner,
        requestTime,
        zoneCategory,
        sessions,
        schedule,
        includeOnDemandStaticFunctionalBookings,
      );

      const timeSlotsGroupedByWorkoutType = this.groupTimeSlotsByWorkoutType(classTimes);

      let availableTimeSlots: IWorkoutTypeClassTimeData[] = this
        .transformAndFilterGroupedTimeSlots(zoneCategory, requestTime, timeSlotsGroupedByWorkoutType);

      function includeOnDemandSlots() {
        if (zoneCategory === 'circuit') {
          return true;
        }
        return isStaticFunctionalZone(zoneCategory);
      }

      if (onDemand.unavailableReason === undefined && includeOnDemandSlots()) {
        BookingsManager.mergeOnDemandSlotsIntoClassTimeArrays(onDemand, availableTimeSlots);
      }

      // ensure the result is in ascending order of duration
      availableTimeSlots = availableTimeSlots.sort((a, b) => {
        if (a.workoutType < b.workoutType) {
          return -1;
        }
        if (a.workoutType > b.workoutType) {
          return 1;
        }
        return 0;
      });

      const status = determineStatus(
        availableTimeSlots,
        zoneCategory,
        withPartner,
      );
      availableStudioSlots.push({
        availableTimeSlots,
        areaCategory: zoneCategory,
        status,
        accordionCondition: determineAccordionCondition(status, zoneCategory),
        warning: determineWarningText(
          zoneCategory,
          withPartner,
          defaultSettings.cardioOrResistance,
          /* userHasABookingOnThisDay */ false,
          useDeprecatedGoalNotAlignedError,
        ),
      });
      timer.done(`Fetching available timeslots for zone-category (${zoneCategory}) and user (${userId})`);
    }

    return availableStudioSlots;
  }

  public async GetAvailableBookingSlotsForGym(
    withPartner: boolean,
    gym: Gym,
    userProfile: UserProfile,
    // HP: this might have been me, but why? it is needed to requerst +x days in advance
    from: Date = new Date(), // TODO: remove
    to?: Date,
    dateOfRequest: Date = new Date(),
    staticZoneType?: number,
    workoutSessions?: UserWorkoutSession[],
    schedule?: IGymSchedule,
  ) {
    return this.GetAvailableBookingSlots(
      withPartner,
      userProfile,
      gym,
      this.calculateDateToUseForSlots(from, dateOfRequest, gym),
      to,
      dateOfRequest,
      /* checkDemerits: */ undefined,
      workoutSessions,
      schedule,
      /* allowEvenLaterLateBookings */ undefined,
      staticZoneType,
    );
  }

  public async GetAvailableBookingSlots(
    withPartner: boolean,
    userProfile: UserProfile,
    gym: Gym = DbGymRepository.getTestGym(),
    from: Date = new Date(),
    to?: Date,
    dateOfRequest: Date = new Date(),
    checkDemerits = true,
    sessions?: UserWorkoutSession[],
    schedule?: IGymSchedule,
    allowEvenLaterLateBookings = false,
    staticZoneType?: number,
  ): Promise<ITimeSlotWithEligibility[]> {
    const timer = this.logger.startTimer();
    if (!to) {
      to = TimeSpan.createEndOfDayDate(from);
    }
    if (checkDemerits && await this.demeritsManager.isPenalisedWithDemerits(userProfile, to, dateOfRequest)) {
      throw BookingError.notAllowed(errorMessage.demerittedNotAllowedToBook);
    }
    const fromTimeRoundedToTheHour = this.roundToHour(from, gym.timezone);

    if (!schedule) {
      schedule = await this.gymScheduler.gymScheduleAt(from, to, gym, dateOfRequest);
    }

    const bookFullSlot = withPartner || gym.singleStationPerZoneGym;
    const userTimeTypes = new UserTimeSlotTypeEligibility(userProfile, gym);
    const timeSlots = await this.calculateTimeSlotsForUser(
      this.getDateOfRequestForSlotCalculation(from, dateOfRequest, allowEvenLaterLateBookings),
      fromTimeRoundedToTheHour,
      to,
      bookFullSlot,
      schedule,
      userProfile,
      DbGymRepository.getGymInfoForTimeslots(gym),
      staticZoneType,
    );
    const mapped = await this.mapBookedTimeSlot(
      userProfile,
      fromTimeRoundedToTheHour,
      timeSlots,
      gym,
      dateOfRequest,
      sessions,
      getAreaCategory(staticZoneType),
    );
    timer.done(
      `Fetching and mapping out timeslots for zone-category (${getAreaCategory(staticZoneType)}) `
      + `and user (${userProfile.uid})`,
    );

    const autoCheckinStartTime = dateOfRequest.getTime()
      + (BookingsConfig.maxNumberOfMinutesPriorToExerciseForCheckin * 60000);
    return mapped
      .map((timeSlot: ITimeSlot): ITimeSlotViewModel => {
        const eligibility = userTimeTypes.checkEligibilityFor(timeSlot.timeSlotType);
        const timeSlotDisplayContent = this.getSlotDisplayContent(timeSlot, gym);
        const autoCheckin = (timeSlot.startTime <= autoCheckinStartTime) && !withPartner;
        return {
          ...timeSlot,
          ...timeSlotDisplayContent,
          bookingType: autoCheckin ? 'instant' : timeSlot.bookingType,
          eligibility,
        };
      });
  }

  public async getAmountOfOpenSlotsAtPoint(
    start: Date,
    end: Date,
    dateOfRequest: Date,
    gym: Gym,
    workoutType: WorkoutType,
    findFullZones: boolean,
  ): Promise<number> {
    // the caller is responsible for rounding the start time correctly
    const schedule = await this.gymScheduler.gymScheduleAt(start, end, gym, dateOfRequest);

    const allowedAmountOfFreeStations: number = await this.allowedAmountOfFreeStations(
      schedule,
      start,
      workoutType,
      findFullZones,
      gym,
    );

    return allowedAmountOfFreeStations;
  }

  public async getAmountOfOpenSlots(
    from: Date,
    to: Date,
    rawGym: Gym,
    dateOfRequest: Date,
    options: Partial<ICalculateTimeSlotsOptions> = {},
  ): Promise<IBaseTimeSlotWithAvailability[]> {
    const gym = DbGymRepository.getGymInfoForTimeslots(rawGym);

    const fromOnHour = this.roundToHour(from);

    const schedule = await this.gymScheduler.gymScheduleAt(from, to, gym, dateOfRequest);

    const fullOptions: ICalculateTimeSlotsOptions = {
      withPartner: false,
      onlyPeakTimeslots: false,
      ...options,
    };

    return this.calculateTimeSlotsForAnyone(dateOfRequest, fromOnHour, to, schedule, gym, fullOptions);
  }

  public async PTBookSingle(
    userEmail: string,
    enabledFeatures: IEnabledFeatures,
    requestedStartTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    allowSameDay = false,
    station: number,
    gym: Gym,
    workoutSettings: WorkoutSettings,
  ): Promise<UserWorkoutSession> {
    const userProfileRepo = this.gymScheduler.getUserProfileRepo();
    const user = await userProfileRepo.getUserByEmail(userEmail);
    if (!user) {
      throw new NotFoundError('user', userEmail);
    }
    const bookingSource = BookingSourceType.PT;
    const gymTypeConfig = GymConfigs[gym.type];
    const amountOfStations = gymTypeConfig.getAmountOfStations(gym);
    let startingStation = station;
    if (requestTime.getTime() > requestedStartTime.getTime()) {
      startingStation = this.gymScheduler.startingStation(
        station,
        requestTime,
        requestedStartTime,
        amountOfStations,
      );
    }

    const ptOverride = true;
    const booking = await this.BookSingle(
      user.uid,
      enabledFeatures,
      requestedStartTime,
      requestTime,
      workoutType,
      bookingSource,
      allowSameDay,
      ptOverride,
      startingStation,
      gym,
      /* isInstant */ false,
      /* isDemo */ false,
      /* timezone */ '',
      workoutSettings,
    );
    await this.markSessionAsCheckedIn(booking, requestTime, true, gym);
    return booking;
  }

  public BookSingleFromParameters(request: IBookingRequest): Promise<UserWorkoutSession> {
    function getGymInfoForTimeslots(): GymInfoForTimeslots | undefined {
      if (request.gym === undefined) {
        return undefined;
      }
      return DbGymRepository.getGymInfoForTimeslots(request.gym);
    }

    return this.BookSingle(
      request.user,
      request.enabledFeatures,
      request.startTime,
      request.requestTime,
      request.workoutType,
      request.bookingSource,
      request.allowSameDay,
      request.ptOverride,
      request.station,
      getGymInfoForTimeslots(),
      request.isInstant,
      request.isDemo,
      request.timezone,
      request.workoutSettings,
      request.isStudioDemoBooking,
      request.playbackSpeed,
      request.externalBookingReference,
      request.isHomeGroupWorkout,
      request.livestreamOptions,
      request.isStreamingWorkout,
      request.pairedSessionReference,
      request.pairedSessionUserProfileId,
      request.areaCategory,
    );
  }

  public async BookSingle(
    userId: string | UserProfile,
    enabledFeatures: IEnabledFeatures,
    requestedStartTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    bookingSource: BookingSourceType,
    allowSameDay = false,
    ptOverride = false,
    station?: number,
    gym: Gym = DbGymRepository.getTestGym(),
    isInstant = false,
    isDemo = false,
    timezone = '',
    workoutSettings?: WorkoutSettings,
    isStudioDemoBooking?: boolean,
    playbackSpeed?: number,
    externalBookingReference?: IExternalReference,
    isHomeGroupWorkout?: boolean,
    livestreamOptions?: ILiveStreamOptions,
    isStreamingWorkout?: boolean,
    pairedSessionReference?: string,
    pairedSessionUserProfileId?: string,
    areaCategory: AreaCategory = 'circuit',
  ): Promise<UserWorkoutSession> {
    if ((areaCategory as string) === '') {
      this.logger.warn('got empty string as AreaCategory, defaulting to circuit');
      areaCategory = 'circuit';
    }

    const user = await this.getUserProfileOrThrow(userId);
    const { uid } = user;

    const staticZoneNumber = getStaticZoneNumberFromArea(areaCategory);

    const settings = await this.getWorkoutSettingsForBooking(
      workoutSettings,
      uid,
      requestedStartTime,
      enabledFeatures,
      gym,
      user,
      areaCategory,
    );

    if (gym.type === GymTypes.Home) {
      let timezoneOffset = 0;
      if (timezone !== '') {
        timezoneOffset = moment.tz(timezone).utcOffset() / 60;
      }
      return this.homeBookingManager.BookSingle(
        user,
        enabledFeatures,
        requestedStartTime,
        workoutType,
        requestTime,
        gym,
        isInstant,
        isDemo,
        timezoneOffset,
        settings,
        playbackSpeed,
        isHomeGroupWorkout,
        livestreamOptions,
        isStreamingWorkout,
        pairedSessionReference,
        pairedSessionUserProfileId,
      );
    }

    const gymTypeConfig = GymConfigs[gym.type];
    await this.preventBookingOnSameDay(
      requestTime,
      requestedStartTime,
      uid,
      allowSameDay,
      undefined,
      gym.timezone,
    );

    const allowEvenLaterLateBookings = ptOverride;
    let targetSlot;
    if (isInstant) {
      targetSlot = await this.getTargetTimeSlotForOnDemandStudioBooking(
        requestedStartTime,
        workoutType,
        user,
        gym.name,
        /* withPartner */ false,
        requestTime,
        areaCategory,
      );
    } else {
      targetSlot = await this.getTargetTimeSlot(
        user,
        /* withPartner */ false,
        workoutType,
        requestedStartTime,
        requestTime,
        gym,
        /* sessions */ undefined,
        /* schedule */ undefined,
        allowEvenLaterLateBookings,
        staticZoneNumber,
      );
    }

    const overrideAccountChecks = ptOverride || isStudioDemoBooking;
    if (!overrideAccountChecks) {
      if (!targetSlot) {
        throw BookingError.slotNotAvailable('No eligible slot found');
      }

      if (!this.subscriptionStillValidForTimeSlot(user, targetSlot.startTime)) {
        throw new HandledError('Subscription is not valid for this timeslot and must be renewed');
      }

      if (await this.demeritsManager.isPenalisedWithDemerits(user, requestedStartTime, requestTime)) {
        throw BookingError.notAllowed(errorMessage.demerittedNotAllowedToBook);
      }
    }

    if (targetSlot && targetSlot.isFull) {
      throw BookingError.slotNotAvailable('The current slot is fully booked');
    }

    // We need to consider locking the targetSlot
    // TODO: replace with UserWorkoutSession.create to remove positional undefined and name parameters
    const workoutSession = new UserWorkoutSession(
      user.uid,
      workoutType,
      settings,
      undefined,
      requestedStartTime,
      undefined,
      requestTime,
      gym,
      externalBookingReference,
      undefined,
      staticZoneNumber,
    );
    workoutSession.memberMainGym = user.gymName;
    await this.checkInSessionForGym(gym, workoutSession, requestTime, true);
    if (station === undefined) {
      station = await this.getPerferredStationForSessionsOnHalfHour(
        workoutSession,
        gymTypeConfig.getAmountOfStations(gym),
      );
    }

    const booked = await this.schedule(
      workoutSession,
      undefined,
      true,
      requestTime,
      undefined,
      undefined,
      station,
      bookingSource,
      staticZoneNumber,
    );
    if (!booked) {
      throw new HandledError('Something went wrong. Please refresh and retry.');
    }

    if (workoutSession.startTime.getTime() !== requestedStartTime.getTime()) {
      await this.cancelSession(workoutSession);
      throw new HandledError('Could not book for specified start time. Please try again.');
    }
    await this.rejectAllReceivedInvitesForSpecifiedDay(user.uid, requestedStartTime, requestTime);
    return workoutSession;
  }

  public async BookWithPartner(
    userId: string | UserProfile,
    enabledFeatures: IEnabledFeatures,
    requestedStartTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    bookingSource: BookingSourceType,
    allowSameDay = false,
    confirmedBookingsForUser?: UserWorkoutSession[],
    schedule?: IGymSchedule,
    gym: Gym = DbGymRepository.getTestGym(),
    workoutSettings?: WorkoutSettings,
    externalBookingReference?: IExternalReference,
    isOnDemandStudioBooking?: boolean,
    isAnonymousPartner: boolean = false,
    areaCategory: AreaCategory = 'circuit',
  ): Promise<UserWorkoutSession> {
    // todo: use externalBookingReference when creating bookings from ClassPass in #2131
    const user = await this.getUserProfileOrThrow(userId);

    if (confirmedBookingsForUser) {
      for (const session of confirmedBookingsForUser) {
        if (session.userProfileId !== user.uid) {
          throw new HandledError('Specified confirmedBookingsForUser does not belong to user');
        }
      }
    }
    const gymTypeConfig = GymConfigs[gym.type];
    await this.preventBookingOnSameDay(
      requestTime,
      requestedStartTime,
      user.uid,
      allowSameDay,
      confirmedBookingsForUser ? confirmedBookingsForUser[0] : undefined,
      gym.timezone,
    );

    const staticZoneNumber = getStaticZoneNumberFromArea(areaCategory);

    let targetSlot;
    if (isOnDemandStudioBooking && isAnonymousPartner) {
      targetSlot = await this.getTargetTimeSlotForOnDemandStudioBooking(
        requestedStartTime,
        workoutType,
        user,
        gym.name,
        true,
        requestTime,
        areaCategory,
      );
    } else if (isOnDemandStudioBooking && !isAnonymousPartner) {
      targetSlot = null;
    } else {
      targetSlot = await this.getTargetTimeSlot(
        user,
        true,
        workoutType,
        requestedStartTime,
        requestTime,
        gym,
        confirmedBookingsForUser,
        schedule,
        undefined,
        staticZoneNumber,
      );
    }

    if (!targetSlot) {
      throw BookingError.slotNotAvailable('No eligible slot found');
    }
    if (!this.subscriptionStillValidForTimeSlot(user, targetSlot.startTime)) {
      throw new HandledError('Subscription is not valid for this timeslot and must be renewed');
    }
    if (targetSlot.isFull) {
      throw BookingError.slotNotAvailable('The current slot is fully booked');
    }
    if (await this.demeritsManager.isPenalisedWithDemerits(user, requestedStartTime, requestTime)) {
      throw BookingError.notAllowed(errorMessage.demerittedNotAllowedToBook);
    }

    const settings = await this.getWorkoutSettingsForBooking(
      workoutSettings,
      user.uid,
      requestedStartTime,
      enabledFeatures,
      gym,
      user,
      areaCategory,
    );

    const workoutSessionLeft = new UserWorkoutSession(
      user.uid,
      workoutType,
      settings,
      undefined,
      requestedStartTime,
      BookingState.ConfirmedBooking,
      requestTime,
      gym,
      undefined,
      undefined,
      staticZoneNumber,
    );
    workoutSessionLeft.memberMainGym = user.gymName;
    await this.checkInSessionForGym(gym, workoutSessionLeft, requestTime, true);

    const workoutSessionRight = this.createProvisionalBooking(
      user.uid,
      workoutType,
      settings,
      requestedStartTime,
      gym,
      requestTime,
      user.gymName,
      staticZoneNumber,
    );

    const station = await this.getPerferredStationForSessionsOnHalfHour(
      workoutSessionLeft,
      gymTypeConfig.getAmountOfStations(gym),
    );

    const booked = await this.schedule(
      workoutSessionLeft,
      workoutSessionRight,
      true,
      requestTime,
      schedule,
      undefined,
      station,
      bookingSource,
      staticZoneNumber,
    );

    if (!booked) {
      throw new HandledError('Something went wrong. Please refresh and retry.');
    }

    if (workoutSessionLeft.startTime.getTime() !== requestedStartTime.getTime()
      || workoutSessionRight.startTime.getTime() !== requestedStartTime.getTime()) {
      await this.cancelSession(workoutSessionLeft, requestTime);
      await this.cancelSession(workoutSessionRight, requestTime);
      throw new HandledError('Could not book for specified start time. Please try again.');
    }

    await this.rejectAllReceivedInvitesForSpecifiedDay(user.uid, requestedStartTime, requestTime);

    return workoutSessionLeft;
  }

  public async InviteAndBookWithPartner(
    partnerId: string,
    userId: string,
    enabledFeatures: IEnabledFeatures,
    startTime: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    allowSameDay = false,
    gym: Gym = DbGymRepository.getTestGym(),
    bookingSource: BookingSourceType,
    workoutSettings: WorkoutSettings,
    areaCategory: AreaCategory = 'circuit',
  ): Promise<UserWorkoutSession> {
    const user = await this.gymScheduler.getUserProfile(userId);
    if (!user) {
      throw new NotFoundError('user', userId);
    }
    const partner = await this.gymScheduler.getUserProfile(partnerId);
    if (!partner) {
      throw new NotFoundError('user', partnerId);
    }
    const bookingStartOfDay: Date = TimeSpan.createStartOfDayDate(startTime);
    const earliestOfNowAndStartOfDay = TimeSpan.max(requestTime, bookingStartOfDay);
    const endOfDay = TimeSpan.createEndOfDayDate(startTime);
    const getConfirmedBookingsForUser = await this.gymScheduler.getConfirmedBookingsForUser(
      userId,
      earliestOfNowAndStartOfDay,
      endOfDay,
      requestTime,
    );
    const confirmedBookingsBeforeWarmdown = getConfirmedBookingsForUser.filter(
      WorkoutFilter.getExcludeWorkoutsDuringWarmdownFilter(requestTime),
    );
    const schedule = await this.gymScheduler.gymScheduleAt(earliestOfNowAndStartOfDay, endOfDay, gym, requestTime);
    const inviterBooking = await this.BookWithPartner(
      userId,
      enabledFeatures,
      startTime,
      requestTime,
      workoutType,
      bookingSource,
      allowSameDay,
      confirmedBookingsBeforeWarmdown,
      schedule,
      gym,
      workoutSettings,
      undefined, /* Booking reference */
      undefined, /* On Demand */
      false,
      areaCategory,
    );
    const provisionalWorkoutId = inviterBooking.pairedSessionReference;
    if (typeof provisionalWorkoutId !== 'string') {
      throw new HandledError('Failed to invite booking since provisional booking was not found');
    } else {
      const returnThis = await this.InvitePartnerToBooking(
        userId,
        partnerId,
        provisionalWorkoutId,
        enabledFeatures,
        requestTime,
        areaCategory,
      );
      return returnThis;
    }
  }

  public async CancelBooking(
    userId: string,
    workoutId: string,
    dateOfRequest: Date = new Date(),
    sessionsFromNow = 0,
    removedReason: RemovedReasonType = RemovedReasonType.Cancelled,
  ): Promise<void> {
    const workoutSession = await this.gymScheduler.getWorkoutSession(workoutId);
    if (!workoutSession) {
      throw NotFoundError.Workout(workoutId);
    }

    await this.cancelBooking(userId, workoutSession, dateOfRequest, sessionsFromNow, removedReason);
  }

  public async CancelBookingByExternalReference(reference: IExternalReference): Promise<void> {
    const workoutSession = await this.gymScheduler.workoutRepository.getFromExternalReferences(reference);
    if (!workoutSession) {
      throw NotFoundError.Workout('externalReferenceId');
    }

    const { userProfileId } = workoutSession;
    return this.cancelBooking(
      userProfileId,
      workoutSession,
      new Date(),
      /* sessionsFromNow */ 0,
      RemovedReasonType.Cancelled,
    );
  }

  public async UndoCancel(
    userId: string,
    workoutId: string,
    dateOfRequest: Date = new Date(),
  ): Promise<UserWorkoutSession> {
    const workoutSession = await this.gymScheduler.getWorkoutSession(workoutId);
    if (!workoutSession) {
      throw new NotFoundError('workout', workoutId);
    }
    if (workoutSession.userProfileId !== userId) {
      throw new HandledError('Workout does not belong to this user');
    }
    if (!workoutSession.removedAt) {
      throw new HandledError('Cannot undo a remove that has not been removed');
    }
    if (workoutSession.removedAt.getTime() <= dateOfRequest.getTime()) {
      throw new HandledError('Cannot undo a remove that has already passed the date');
    }
    setBookingsRemovedState(workoutSession, false);
    await this.gymScheduler.updateWorkoutSession(workoutSession);
    return workoutSession;
  }

  public async ChangeBooking(
    userId: string,
    workoutId: string,
    enabledFeatures: IEnabledFeatures,
    changeToDate: Date,
    changeToWorkoutType: WorkoutType,
    dateOfChange: Date,
    gym: Gym,
    workoutSettings?: WorkoutSettings,
    isStudioDemo?: boolean,
  ):
    Promise<UserWorkoutSession> {
    const workoutSession = await this.gymScheduler.getWorkoutSession(workoutId);
    if (!workoutSession) {
      throw new HandledError('No relevant session found');
    }

    if (UserWorkoutSession.isHomeGroupBooking(workoutSession)) {
      throw new BadRequestError(
        { details: 'Not allowed to change a group booking, cancel and book another workout' },
        ErrorCode.NotAllowedToChangeGroupBooking,
      );
    }

    if (workoutSession.userProfileId !== userId) {
      throw new HandledError('Workout does not belong to this user');
    }

    if (gym.type === GymTypes.Home) {
      return this.homeBookingManager.ChangeBooking(
        userId,
        enabledFeatures,
        workoutSession,
        changeToDate,
        changeToWorkoutType,
        dateOfChange,
        gym,
        workoutSettings,
      );
    }
    const { settings, bookingSource } = workoutSession;
    const newSession = await this.BookSingle(
      userId,
      enabledFeatures,
      changeToDate,
      dateOfChange,
      changeToWorkoutType,
      bookingSource,
      true,
      undefined,
      undefined,
      gym,
      false,
      false,
      undefined,
      workoutSettings,
      isStudioDemo,
    );
    newSession.settings = settings;
    await this.gymScheduler.updateWorkoutSession(newSession);
    await this.CancelBooking(userId, workoutId, dateOfChange);
    return newSession;
  }

  public async PTCheckIn(
    gym: Gym,
    userId: string,
    checkInTime: Date,
    allowBagContact = false,
  ): Promise<UserWorkoutSession> {
    const allowedToCheckinEarly = true;
    return this.CheckIn(gym, userId, checkInTime, allowBagContact, true, allowedToCheckinEarly);
  }

  public async CheckInFromApp(gym: Gym, userId: string, checkInTime: Date): Promise<UserWorkoutSession> {
    return this.CheckIn(gym, userId, checkInTime, 'unchanged', true, false);
  }

  public async CheckIn(
    gym: Gym,
    userId: string,
    checkInTime: Date,
    allowBagContact: boolean | 'unchanged',
    skippedBiometric = false,
    allowedToCheckinEarly = false,
  ): Promise<UserWorkoutSession> {
    const details = await this.gymScheduler.getBookingDetailForNextCheckIn(userId, checkInTime, gym);
    if (!details) {
      throw new HandledError(errorMessage.checkInTooEarly);
    } else if (details.canCheckIn === CanCheckIn.TooEarly && allowedToCheckinEarly) {
      this.logger.warn(`allowing earlier checkin for ${userId}`, {
        sessionId: details.booking.uniqueReference,
        checkInTime,
        earliestCheckIn: details.earliestCheckIn,
      });
    } else if (details.canCheckIn !== CanCheckIn.Yes) {
      throw new HandledError('Not allowed to checkin to the session at the current time');
    }

    const relevantSession = details.booking;

    this.logger.info('checking in user', {
      userId,
      sessionId: relevantSession.uniqueReference,
      checkInTime,
      allowBagContact,
      skippedBiometric,
    });
    if (allowBagContact !== 'unchanged') {
      relevantSession.settings.allowBagContact = allowBagContact;
    } else if (relevantSession.settings.allowBagContact === undefined) {
      relevantSession.settings.allowBagContact = false;
    }
    await this.markSessionAsCheckedIn(relevantSession, checkInTime, skippedBiometric, gym);
    await this.gymScheduler.updateWorkoutSession(relevantSession);
    const user = await this.gymScheduler.getUserProfileRepo().get(userId);
    if (user && !user.ptApprovedAt) {
      user.ptApprovedAt = Date.now();
      await this.gymScheduler.getUserProfileRepo().update(user);
    }

    return relevantSession;
  }

  public async CheckInUserWorkoutSession(
    workoutId: string,
    checkInTime: Date,
    gym: Gym = DbGymRepository.getTestGym(),
  ): Promise<UserWorkoutSession> {
    const relevantSession = (await this.gymScheduler.getWorkoutSession(workoutId));
    if (relevantSession) {
      await this.markSessionAsCheckedIn(relevantSession, checkInTime, false, gym);
    } else {
      throw new HandledError('No relevant session found');
    }
    return relevantSession;
  }

  public async InvitePartnerToBooking(
    userId: string | UserProfile,
    partnerUserId: string | UserProfile,
    provisionalWorkoutId: string,
    enabledFeatures: IEnabledFeatures,
    dateOfRequest: Date = new Date(),
    areaCategory: AreaCategory = 'circuit',
  ): Promise<UserWorkoutSession> {
    let user;
    if (typeof (userId) === 'string') {
      user = await this.gymScheduler.getUserProfile(userId);
    } else {
      user = userId;
    }
    if (!user) {
      throw new HandledError('Invalid userId');
    }

    let partner;
    if (typeof (partnerUserId) === 'string') {
      partner = await this.gymScheduler.getUserProfile(partnerUserId);
    } else {
      partner = partnerUserId;
    }

    if (!partner) {
      throw new HandledError('Invalid partnerUserId');
    }
    const provisionalWorkout = await this.gymScheduler.getWorkoutSession(provisionalWorkoutId);
    if (!provisionalWorkout) {
      throw new HandledError(`No workout found with id ${provisionalWorkoutId}`);
    }
    if (!provisionalWorkout.isProvisionalInvite() || !provisionalWorkout.pairedSessionReference) {
      throw new HandledError('Specified workout is not a provisional workout');
    }

    const requestedStartTime = provisionalWorkout.startTime;
    const gym = DbGymRepository.getByName(provisionalWorkout.gymName);
    if (!(await this.isUserEligibleForProvisionalBooking(
      partner,
      UserWorkoutSession.hasPairedWorkoutSession(provisionalWorkout),
      provisionalWorkout.workoutType,
      requestedStartTime,
      dateOfRequest,
      gym,
    ))) {
      throw new HandledError('Partner is not allowed in this timeslot');
    }

    if (!(await this.isAvailableForBooking(
      partner,
      requestedStartTime,
      dateOfRequest,
      user,
    ))) {
      throw new HandledError('Partner user is not available for this booking');
    }
    const userWorkout = await this.gymScheduler.getWorkoutSession(provisionalWorkout.pairedSessionReference);
    if (!userWorkout) {
      throw new NotFoundError('workout', provisionalWorkout.pairedSessionReference);
    }

    const staticZoneNumber = getStaticZoneNumberFromArea(areaCategory);

    const partnerInviteWorkout = new UserWorkoutSession(
      partner.uid,
      provisionalWorkout.workoutType,
      ProgramsConfig.fallbackSettingsForGoal(user.longTermGoal, user.skillLevel),
      undefined,
      provisionalWorkout.startTime,
      BookingState.BookingInvite,
      dateOfRequest,
      DbGymRepository.getByName(userWorkout.gymName),
      undefined,
      undefined,
      staticZoneNumber,
    );
    partnerInviteWorkout.expiresAt = provisionalWorkout.expiresAt;
    partnerInviteWorkout.memberMainGym = partner.gymName;
    await this.cancelSession(provisionalWorkout, dateOfRequest);
    // notify the old invitee that you have cancelled
    if (provisionalWorkout.bookingState === BookingState.BookingInvite
      && userId !== provisionalWorkout.userProfileId && this.notificationService) {
      const details = {
        inviterUserId: user.uid,
        inviteeUserId: provisionalWorkout.userProfileId,
        bookingReference: provisionalWorkout.uniqueReference,
        gymTimezone: gym.timezone,
      };
      this.handleNotificationResultInBackground(this.notificationService.cancelInvitation(details), details);
    }

    const station = userWorkout.exerciseSessions[0].stationNumber;
    await this.schedule(
      userWorkout,
      partnerInviteWorkout,
      false,
      dateOfRequest,
      undefined,
      undefined,
      station,
      undefined,
      staticZoneNumber,
    );
    if (this.notificationService) {
      const details = {
        inviterUserId: user.uid,
        inviteeUserId: partner.uid,
        bookingReference: partnerInviteWorkout.uniqueReference,
        gymTimezone: gym.timezone,
      };
      this.handleNotificationResultInBackground(this.notificationService.invite(details), details);
    }
    await this.partnersManager.addPartner(user.uid, partnerUserId);
    return partnerInviteWorkout;
  }

  public async AcceptOpenInvite(
    userId: string,
    workoutId: string,
    enabledFeatures: IEnabledFeatures,
    requestTime: Date,
    useInvitersSettings = true,
  ): Promise<UserWorkoutSession> {
    return this.homeBookingManager.AcceptInvite(
      userId,
      workoutId,
      enabledFeatures,
      requestTime,
      useInvitersSettings,
    );
  }

  public async AcceptInvite(
    userId: string,
    provisionalWorkoutId: string,
    acceptInviterSettings = false,
    dateOfRequest: Date = new Date(),
    enabledFeatures: IEnabledFeatures,
  ): Promise<UserWorkoutSession> {
    const provisionalWorkout = await this.gymScheduler.getWorkoutSession(provisionalWorkoutId);

    if (!provisionalWorkout) {
      throw new NotFoundError('workout', provisionalWorkoutId);
    }
    if (!provisionalWorkout.isProvisionalInvite()) {
      throw new HandledError('Specified workout is not a ProvisionalBooking or BookingInvite');
    }
    if (!provisionalWorkout.pairedSessionReference) {
      throw new HandledError('Invite no longer available');
    }
    if (provisionalWorkout.userProfileId !== userId) {
      throw new HandledError('Specified workout does not belong to user');
    }
    const user = await this.gymScheduler.getUserProfile(userId);
    if (!user) {
      throw NotFoundError.User(userId);
    }
    const gym = DbGymRepository.getByName(provisionalWorkout.gymName);
    const defaultToAllRounder = user.longTermGoal ?? ProgramType.GetStrongAndFitGoal;
    let recommendedSettings = await this.gymScheduler
      .workoutProgramRecommendations.GetRecommendedSettingsForDate(
        userId,
        provisionalWorkout.startTime,
        enabledFeatures,
        gym,
        defaultToAllRounder,
        new Date(),
      );
    if (!recommendedSettings) {
      recommendedSettings = ProgramsConfig.fallbackSettingsForGoal(user.longTermGoal, user.skillLevel);
    }
    WorkoutProgramRecommendations.forceSettingsBasedOnWorkoutArea(
      recommendedSettings,
      provisionalWorkout.areaCategory ?? 'circuit',
    );
    if (!recommendedSettings) {
      throw new HandledError(`No recommended settings for workout id ${provisionalWorkoutId}`);
    }
    if (acceptInviterSettings) {
      await this.applyInvitersWorkoutAndSettingsToInvitee(
        provisionalWorkout,
        provisionalWorkout.pairedSessionReference,
        recommendedSettings,
      );
    } else {
      provisionalWorkout.settings = recommendedSettings;
    }

    await this.checkInSessionForGym(gym, provisionalWorkout, dateOfRequest, true);

    provisionalWorkout.bookingState = BookingState.ConfirmedBooking;
    delete provisionalWorkout.expiresAt;
    await this.gymScheduler.updateWorkoutSession(provisionalWorkout);

    if (this.notificationService) {
      const details = {
        inviterUserId: provisionalWorkout.pairedSessionUserProfileId!,
        inviteeUserId: userId,
        bookingReference: provisionalWorkout.uniqueReference,
        gymTimezone: gym.timezone,
      };
      this.handleNotificationResultInBackground(this.notificationService.acceptInvitation(details), details);
    }
    await this.rejectAllReceivedInvitesForSpecifiedDay(user.uid, provisionalWorkout.startTime, dateOfRequest);
    await this.partnersManager.addPartner(user.uid, provisionalWorkout.pairedSessionUserProfileId!);
    return provisionalWorkout;
  }

  public async RejectInvite(
    userId: string,
    provisionalWorkoutId: string,
    dateOfRequest: Date = new Date(),
  ): Promise<void> {
    const user = await this.gymScheduler.getUserProfile(userId);
    if (!user) {
      throw new HandledError('Invalid userId');
    }

    const provisionalWorkout = await this.gymScheduler.getWorkoutSession(provisionalWorkoutId);
    if (!provisionalWorkout) {
      throw new HandledError(`No workout found with id ${provisionalWorkoutId}`);
    }

    if (!provisionalWorkout.isProvisionalInvite()) {
      throw new HandledError('Workout is in incorrect state to do this');
    }
    if (!provisionalWorkout.pairedSessionReference) {
      throw new HandledError('Provisional workout does not have a pairedSessionReference');
    }
    const pairedSession = await this.gymScheduler.getWorkoutSession(provisionalWorkout.pairedSessionReference);
    if (!pairedSession) {
      throw new HandledError(`Paired session with reference
                ${provisionalWorkout.pairedSessionReference} not found `);
    }
    await this.cancelSession(provisionalWorkout, dateOfRequest);
    if (dateOfRequest.getTime() < provisionalWorkout.expiresAt!.getTime()) {
      const newProvisionalWorkout = this.createProvisionalBooking(
        pairedSession.userProfileId,
        pairedSession.workoutType,
        Object.assign(new WorkoutSettings(), pairedSession.settings),
        pairedSession.startTime,
        DbGymRepository.getByName(pairedSession.gymName),
        dateOfRequest,
        pairedSession.gymName,
      );
      newProvisionalWorkout.expiresAt = provisionalWorkout.expiresAt;
      const booked = await this.schedule(pairedSession, newProvisionalWorkout, false, dateOfRequest);
      if (!booked) {
        throw new HandledError('Something went wrong. Please refresh and retry.');
      }
      if (newProvisionalWorkout.startTime.getTime() !== pairedSession.startTime.getTime()) {
        await this.cancelSession(pairedSession, dateOfRequest);
        await this.cancelSession(newProvisionalWorkout, dateOfRequest);
        throw new HandledError('Could not book for specified start time. Please try again.');
      }
      if (this.notificationService) {
        const gym = DbGymRepository.getByName(provisionalWorkout.gymName);
        const details = {
          inviterUserId: pairedSession.userProfileId,
          inviteeUserId: userId,
          bookingReference: pairedSession.uniqueReference,
          gymTimezone: gym.timezone,
        };
        this.handleNotificationResultInBackground(this.notificationService.rejectInvitation(details), details);
      }
    }
  }

  public async CancelInvite(
    userId: string,
    workoutId: string,
    dateOfRequest: Date = new Date(),
    sessionsFromNow = 0,
  ): Promise<void> {
    const user = await this.gymScheduler.getUserProfile(userId);
    if (!user) {
      throw new HandledError('Invalid userId');
    }
    const userWorkout = await this.gymScheduler.getWorkoutSession(workoutId);
    if (!userWorkout) {
      throw new HandledError(`No workout found with id ${workoutId}`);
    }
    if (userWorkout.userProfileId !== userId) {
      throw new HandledError('Specified workout does not belong to user');
    }
    if (!userWorkout.pairedSessionReference) {
      throw new HandledError('Specified workout does not have a paired session reference');
    }
    const provisionalWorkout = await this.gymScheduler.getWorkoutSession(userWorkout.pairedSessionReference);
    if (!provisionalWorkout) {
      throw new HandledError(`No workout found with id ${userWorkout.pairedSessionReference}`);
    }

    if (!provisionalWorkout.isProvisionalInvite()) {
      throw new HandledError('Specified workout is not a ProvisionalBooking or BookingInvite');
    }
    if (!provisionalWorkout.pairedSessionReference) {
      throw new HandledError('Provisional workout does not have a pairedSessionReference');
    }

    await this.cancelSession(provisionalWorkout, dateOfRequest, sessionsFromNow, RemovedReasonType.Cancelled);

    if (userId !== provisionalWorkout.userProfileId && this.notificationService) {
      const gym = DbGymRepository.getByName(provisionalWorkout.gymName);
      const details = {
        inviterUserId: userId,
        inviteeUserId: provisionalWorkout.userProfileId,
        bookingReference: provisionalWorkout.uniqueReference,
        gymTimezone: gym.timezone,
      };
      this.handleNotificationResultInBackground(this.notificationService.cancelInvitation(details), details);
    }
  }

  public async SearchForPartnerToInviteToWorkout(
    usernameOrFullnameOrNickname: string,
    userId: string,
    workoutType: WorkoutType,
    workoutStartTime: Date,
    gym: Gym,
    dateOfRequest: Date = new Date(),
  )
    : Promise<IDisplayableUserProfile[]> {
    if (usernameOrFullnameOrNickname.length < BookingsConfig.minNumberOfCharactersForSearch) {
      throw new HandledError('Can not perform search on query with'
        + ` less than ${BookingsConfig.minNumberOfCharactersForSearch} characters`);
    }
    const currUser = await this.gymScheduler.getUserProfile(userId);
    const userProfiles: UserProfile[] = await this.gymScheduler.searchUserProfile(usernameOrFullnameOrNickname);
    const filteredProfiles = userProfiles.filter(
      (user): user is UserProfile =>
        !!currUser
        && this.fetchUserOrganisation(user) === this.fetchUserOrganisation(currUser),
    );

    return this.determineAvailabilityForUsersForPartner(
      filteredProfiles,
      userId,
      workoutType,
      workoutStartTime,
      DbGymRepository.getGymInfoForTimeslots(gym),
      dateOfRequest,
    );
  }

  public async AvailabilitySearchForWhitelistedPartners(
    userId: string,
    workoutType: WorkoutType,
    workoutStartTime: Date,
    gym: Gym,
    dateOfRequest?: Date,
  ): Promise<IDisplayableUserProfile[]> {
    const userProfiles = await Promise.all((await this.partnersManager.getPartners(userId))
      .map(async (user: IDisplayableUserProfile) => {
        try {
          const up = await this.gymScheduler.getUserProfile(user.uid);
          if (!up) {
            return Object.assign(new UserProfile(), user);
          }
          return up;
        } catch (err) {
          return Object.assign(new UserProfile(), user);
        }
      }));
    return this.determineAvailabilityForUsersForPartner(
      userProfiles,
      userId,
      workoutType,
      workoutStartTime,
      DbGymRepository.getGymInfoForTimeslots(gym),
      dateOfRequest,
    );
  }

  public async isAvailableForBooking(
    user: UserProfile,
    startTime: Date,
    dateOfRequest: Date = new Date(),
    queryingUser?: UserProfile,
    sessions?: UserWorkoutSession[],
  ): Promise<boolean> {
    if (queryingUser
      && (await this.partnersManager.isInBlacklist(user, queryingUser)
        || await this.partnersManager.isPrivate(user, queryingUser.uid))) {
      return false;
    }
    if (await this.demeritsManager.isPenalisedWithDemerits(user, startTime, dateOfRequest)) {
      return false;
    }
    const bookingStartOfDay = TimeSpan.createStartOfDayDate(startTime);
    let nextBooking;
    if (sessions) {
      nextBooking = (sessions.filter(
        (session: UserWorkoutSession) => session.startTime.getTime() >= TimeSpan.max(
          dateOfRequest,
          bookingStartOfDay,
        ).getTime(),
      ))[0];
    } else {
      nextBooking = (await this.gymScheduler
        .getConfirmedBookingsForUser(
          user.uid,
          TimeSpan.max(dateOfRequest, bookingStartOfDay),
          undefined,
          dateOfRequest,
        ))[0];
    }
    if (nextBooking && TimeSpan.areOnSameDateSameDay(startTime, nextBooking.startTime)) {
      return false;
    }

    const fromDate = (new Date(bookingStartOfDay.getTime()));
    fromDate.setHours(0, 0, 0, 0);
    const toDate = TimeSpan.createEndOfDayDate(fromDate);

    const inviteSessionsForDay = await this.gymScheduler.getInvitesForUser(
      user.uid,
      fromDate,
      toDate,
    );
    if (inviteSessionsForDay.length >= BookingsConfig.maxNumberOfBookingInvitesForUser) {
      return false;
    }
    return true;
  }

  public async getTargetTimeSlot(
    user: UserProfile,
    withPartner: boolean,
    workoutType: WorkoutType,
    bookingStartTime: Date,
    dateOfRequest: Date,
    gym: Gym,
    sessions?: UserWorkoutSession[],
    schedule?: IGymSchedule,
    allowEvenLaterLateBookings = false,
    staticZoneType?: number,
  ): Promise<ITimeSlot | undefined> {
    const userTimeTypes = new UserTimeSlotTypeEligibility(user, gym);
    const availableSlots = await this.GetAvailableBookingSlots(
      withPartner,
      user,
      gym,
      bookingStartTime,
      bookingStartTime,
      dateOfRequest,
      false,
      sessions,
      schedule,
      allowEvenLaterLateBookings,
      staticZoneType,
    );

    return this.getTargetSlotFromAvailableSlots(
      bookingStartTime,
      workoutType,
      availableSlots,
      userTimeTypes,
      withPartner,
      staticZoneType !== undefined,
    );
  }

  public async NotifyExpiredSessions(dateToCheckFor: Date, dateOfRequest: Date): Promise<UserWorkoutSession[]> {
    this.logger.info('notifying expired sessions', dateToCheckFor);
    const sessions = await this.gymScheduler.getNotNotifiedExpiredWorkoutSessions(dateToCheckFor, dateOfRequest);
    const notified: UserWorkoutSession[] = [];
    if (this.notificationService) {
      for (const session of sessions) {
        let details;
        try {
          if (!session.pairedSessionUserProfileId || !session.userProfileId) {
            // nothing to notify
          } else if (session.pairedSessionUserProfileId === session.userProfileId) {
            this.logger.warn(`the inviter and the invitee is the same user for ${session.uniqueReference}`);
          } else {
            // this is reversed, because this session is the invitee's
            // thus the user here is the invitee
            // and the paired session's userId was the inviter
            const gym = DbGymRepository.getByName(session.gymName);
            details = {
              inviterUserId: session.pairedSessionUserProfileId!,
              inviteeUserId: session.userProfileId,
              bookingReference: session.uniqueReference,
              gymTimezone: gym.timezone,
            };
            try {
              this.logger.info('attempting to notify about expired session', details);
              const resultSession = await this.handleNotificationResult(
                this.notificationService!.invitationExpired(details),
                details,
              );
              this.logger.info('notified expired session', details, resultSession);
            } catch (err) {
              this.logger.error('failed to notify', details, err);
            }
            notified.push(session);
          }

          session.expiryNotified = true;
          await this.gymScheduler.updateWorkoutSession(session);
        } catch (err) {
          this.logger.error('failed to notify about expired session', details, err);
          return err;
        }
      }
    }
    return notified;
  }

  public async ChangeBookingInWholeZone(
    uid: string,
    workoutId: string,
    enabledFeatures: IEnabledFeatures,
    changeToStartDate: Date,
    changeToWorkoutType: WorkoutType,
    requestTime: Date,
    gym: Gym,
    bookingSource: BookingSourceType,
    workoutSettings?: WorkoutSettings,
  ): Promise<UserWorkoutSession> {
    const previousBooking = await this.gymScheduler.getWorkoutSession(workoutId);
    if (previousBooking == null) {
      throw NotFoundError.Workout(workoutId);
    }
    await this.CancelBooking(uid, workoutId, requestTime);
    const settings = workoutSettings || previousBooking.settings;
    const workoutBookingSource = bookingSource || previousBooking.bookingSource;
    const newBooking = await this.BookSingleInWholeZone(
      uid,
      enabledFeatures,
      changeToStartDate,
      requestTime,
      changeToWorkoutType,
      gym,
      workoutBookingSource,
      settings,
    );
    await this.gymScheduler.updateWorkoutSession(newBooking);
    return newBooking;
  }

  public async BookSingleInWholeZone(
    uid: string | UserProfile,
    enabledFeatures: IEnabledFeatures,
    startDate: Date,
    requestTime: Date,
    workoutType: WorkoutType,
    gym: Gym,
    bookingSource: BookingSourceType,
    workoutSettings?: WorkoutSettings,
    externalBookingReference?: IExternalReference,
    isInstant?: boolean,
  ): Promise<UserWorkoutSession> {
    const booking = await this.BookWithPartner(
      uid,
      enabledFeatures,
      startDate,
      requestTime,
      workoutType,
      bookingSource,
      undefined,
      undefined,
      undefined,
      gym,
      workoutSettings,
      externalBookingReference,
      isInstant,
      true,
    );
    return this.delinkProvisionalPairedBookingAndExpireNow(booking.uniqueReference, requestTime);
  }

  public async DiscardHomeWorkout(workoutId: string, now: Date): Promise<void> {
    const workout = await this.gymScheduler.workoutRepository.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }

    workout.endTime = now;
    setBookingsRemovedState(workout, now, RemovedReasonType.Cancelled);
    await this.gymScheduler.workoutRepository.update(workout);
  }

  public async GetOpenInviteBookingDetails(
    userId: string,
    workoutId: string,
    dateOfRequest: Date,
  ): Promise<IDisplayableOpenInviteDetails> {
    const currentWorkout = await this.gymScheduler.workoutRepository.getWorkoutSessionFromIdWithPairedSession(
      workoutId,
    );
    if (!currentWorkout) {
      throw NotFoundError.Workout(workoutId);
    }

    let workoutToUseForInvite = currentWorkout;
    if (UserWorkoutSession.isHomeGroupBooking(workoutToUseForInvite) && workoutToUseForInvite.pairedSession) {
      workoutToUseForInvite = workoutToUseForInvite.pairedSession;
    }

    const inviter = await this.gymScheduler.getUserProfileRepo().get(currentWorkout.userProfileId);
    if (!inviter) {
      throw new NotFoundError('User', workoutToUseForInvite.userProfileId);
    }

    const gym = DbGymRepository.getByName(workoutToUseForInvite.gymName);

    const workoutInviteError = await this.homeBookingManager.getOpenInviteWorkoutError(
      userId,
      workoutToUseForInvite,
      dateOfRequest,
    );
    return {
      workoutInviteError,
      referredByUserId: inviter.uid,
      referredByFullname: inviter.fullname,
      referredByFirstname: inviter.firstname,
      referredByBio: inviter.bio,
      landingPage: gym.defaultHomeSignupFlow ? undefined : gym.landingPage,
      workoutInviteGymName: workoutToUseForInvite.gymName,
      workoutInviteId: workoutToUseForInvite.uniqueReference,
      workoutInviteDuration: workoutToUseForInvite.workoutType,
      workoutInviteStartTime: workoutToUseForInvite.startTimeOfFirstExercise.getTime(),
      workoutSettings: workoutToUseForInvite.settings,
      isStreamingWorkout: workoutToUseForInvite.isStreamingWorkout,
      livestreamOptions: workoutToUseForInvite.livestreamOptions,
      utm_source: encodeURIComponent(inviter.signupSource?.attributionSourceId || inviter.email),
      personalisedHeader: gym.personalisedHeader,
      utm_medium: KnownMediums.workoutInvite,
      inviteDate: new Date().toISOString(),
    };
  }

  public async SetInviterOpenInviteLink(workoutId: string, openInviteLink: string): Promise<UserWorkoutSession> {
    const workout = await this.gymScheduler.workoutRepository.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }
    workout.openInviteLink = openInviteLink;
    await this.gymScheduler.workoutRepository.update(workout);
    return workout;
  }

  public async GetOpenInviteClassList(
    userId: string,
    workoutId: string,
    requestTime: Date,
  ): Promise<IDisplayableUserProfile[]> {
    const workout = await this.gymScheduler.workoutRepository.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }

    const user = await this.gymScheduler.getUserProfileRepo().get(userId);
    if (!user) {
      throw new NotFoundError('User', userId);
    }

    if (!UserWorkoutSession.isHomeGroupBooking(workout)
      && !UserWorkoutSession.hasPairedWorkoutSession(workout)) {
      throw new HandledError('Cannot fetch class list for solo workout');
    }

    const { userProfiles, pairedSessions } = await this.getOpenInviteWorkoutsAndUserProfiles(workout, requestTime);
    const openInviteUserProfiles: IDisplayableUserProfile[] = [];
    for (const userProfile of userProfiles) {
      const session = pairedSessions.find(({ userProfileId }) => userProfile.uid === userProfileId);
      if (!session) {
        throw new Error('Failed find session after mapping');
      }
      const isInWhiteList = await this.partnersManager.isInWhitelist(user, userProfile.uid);
      const profile = DisplayableUserProfile.AsDisplayableUserProfile(userProfile, isInWhiteList);
      profile.available = true;
      profile.eligable = true;
      profile.accepted = session.bookingState === BookingState.ConfirmedBooking;
      openInviteUserProfiles.push(profile);
    }
    return openInviteUserProfiles;
  }

  public async GetOpenInviteClassWithWorkoutSettings(
    workout: UserWorkoutSession,
    requestTime: Date,
  ): Promise<IUserWorkoutSessionProfile[]> {
    const { userProfiles, pairedSessions } = await this.getOpenInviteWorkoutsAndUserProfiles(workout, requestTime);
    return pairedSessions.map((session) => {
      const userProfile = userProfiles.find(({ uid }) => uid === session.userProfileId);
      return {
        userProfileId: userProfile ? userProfile.uid : '',
        fullname: userProfile ? userProfile.fullname : '',
        workoutId: session.uniqueReference,
        workoutType: session.workoutType,
        startTimeOfFirstExercise: session.startTimeOfFirstExercise,
        cardioOrResistance: session.settings.cardioOrResistance,
      };
    });
  }

  public ResumeHomeWorkout(workoutId: string, pauseStart: Date | null, pauseResume: Date)
    : Promise<UserWorkoutSession> {
    return this.homeBookingManager.resumeHomeWorkout(workoutId, pauseStart, pauseResume);
  }

  public async GetGymSessionWorkoutInfo(
    gym: Gym,
    start: Date,
    workoutType: WorkoutType,
  ): Promise<IUserSessionHistoryEntry[]> {
    const allSessions = await this.gymScheduler.getAllGymSessions(gym, start, workoutType);

    const result: IUserSessionHistoryEntry[] = [];

    for (const session of allSessions) {
      // todo: check performance on this, might need better ways to merge UserWorkoutSession and UserProfile
      const user = await this.gymScheduler.getUserProfile(session.userProfileId);
      if (!user) {
        continue;
      }

      const removedBeforeStart = session.removedAt !== false
        && session.removedAt < session.startTimeOfFirstExercise;

      result.push({
        userId: session.userProfileId,
        firstExerciseTime: session.startTimeOfFirstExercise,
        bookingState: session.bookingState,
        isPtApproved: UserProfile.isUserPtApproved(user),
        checkedIn: session.checkedIn,
        cancelled: removedBeforeStart,
        userExternalReference: user.externalReference,
        sessionExternalReference: session.externalReference,
      });
    }

    return result;
  }

  public async getBookingsForDay(
    requestTime: Date,
    workoutStartTime: Date,
    userId: string,
  ): Promise<UserWorkoutSession[]> {
    const bookingStartOfDay: Date = TimeSpan.createStartOfDayDate(workoutStartTime);
    const earliestOfNowAndStartOfDay = TimeSpan.max(requestTime, bookingStartOfDay);
    const endOfDay = TimeSpan.createEndOfDayDate(workoutStartTime);
    const confirmedBookingsForUser = await this.gymScheduler.getConfirmedBookingsForUser(
      userId,
      earliestOfNowAndStartOfDay,
      endOfDay,
      requestTime,
    );
    const filteredBooking = confirmedBookingsForUser.filter(
      WorkoutFilter.getExcludeWorkoutsDuringWarmdownFilter(requestTime),
    );
    if (filteredBooking.length === 0) {
      throw new HandledError(`Could not find existing booking on date ${workoutStartTime}`);
    }
    return filteredBooking;
  }

  public async getBookingWithPartner(
    workoutId: string,
    userId: string,
  ): Promise<UserWorkoutSession | undefined> {
    const workout = await this.gymScheduler.workoutRepository.getWorkoutSessionFromIdWithPairedSession(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }

    if (userId === workout.userProfileId
      || (workout.pairedSessionUserProfileId && workout.pairedSessionUserProfileId === userId)) {
      return workout;
    }
    throw new Error(`${userId} not allowed to access ${workoutId}`);
  }

  public async getOnDemandBookingSlotData(
    gym: GymInfoForTimeslots,
    userProfile: UserProfile,
    requestTime: Date,
    userRequestedStartOfDay: Date,
    partnerBooking: boolean,
    userHasExistingBookingForDay: boolean,
    areaCategory: AreaCategory = 'circuit',
  ): Promise<OnDemandBookingSlotData> {
    const timer = this.logger.startTimer();
    function buildUnavailable(reason: UnavailableReason): INoOnDemandSlotAvailable {
      return { unavailableReason: reason };
    }

    if (userHasExistingBookingForDay) {
      return buildUnavailable(UnavailableReason.existingBookingForDay);
    }

    if (gym.enableOnDemandStudioBookings === 'Never') {
      return buildUnavailable(UnavailableReason.notEnabledOnGym);
    }

    const requestingSlotsForToday = TimeSpan.areOnSameDayInTimeZone(requestTime, userRequestedStartOfDay, gym.timezone);
    if (!requestingSlotsForToday) {
      return buildUnavailable(UnavailableReason.notCurrentDay);
    }

    if (partnerBooking) {
      return buildUnavailable(UnavailableReason.partnerBooking);
    }

    const nextAvailableOnDemandSlots = await this.getNextFreeSlotsForOnDemandBookingsInGymForUser(
      gym,
      userProfile.uid,
      requestTime,
      areaCategory,
    );

    if (nextAvailableOnDemandSlots.length === 0) {
      return buildUnavailable(UnavailableReason.noAvailableSpaceInGym);
    }
    timer.done(
      `Mapped the booked timeslots for user (${userProfile.uid})`,
    );
    return {
      slots: nextAvailableOnDemandSlots,
    };
  }

  public async sendCheckInNotifications(time: Date): Promise<void> {
    this.logger.debug('start sending check in notifications');

    if (!this.notificationService) {
      this.logger.error('No notification service found');
      return;
    }

    try {
      const oneHourInMilliseconds = 60 * 60 * 1000;
      const oneHourFromNow = new Date(time.getTime() + oneHourInMilliseconds);

      const gymCheckinData = await this.gymScheduler.GetSessionsNotCheckedInToNotify(oneHourFromNow);

      const usersToSendEmailsTo = gymCheckinData.map((workoutDetails: UserWorkoutSession) => {
        const checkInDetails = this.gymScheduler.getCheckinDataForBooking(workoutDetails.startTime, time);
        if (checkInDetails.canCheckIn === CanCheckIn.Yes && workoutDetails.memberMainGym) {
          const gym = DbGymRepository.getByName(workoutDetails.gymName);
          return {
            uid: workoutDetails.userProfileId,
            startTime: workoutDetails.startTime,
            gymTimezone: gym.timezone,
            location: gym.displayName,
            memberMainGym: workoutDetails.memberMainGym,
            workoutDetails,
          };
        }
        return null;
      });

      const usersToSendEmails = usersToSendEmailsTo.filter((user) => user !== null);

      if (usersToSendEmails.length === 0) {
        return;
      }

      this.logger.info(`[sendCheckInNotifications] sending ${usersToSendEmails.length} check-in reminder emails`);
      for (const user of usersToSendEmails) {
        if (!user) {
          // possible for type only
          continue;
        }

        const memberMainGym = DbGymRepository.getByName(user.memberMainGym);
        const isHomeBooking = UserWorkoutSession.isHomeBooking(user.workoutDetails, DbGymRepository.getByName);
        if (memberMainGym.type === 'Home' && isHomeBooking) {
          this.logger.debug('[bookingsmanager] sending'
            + `${user.uid} home check-in reminder emails`);
          this.notificationService.sendHomeCheckInNotifications(user)
            .catch((err: unknown) => {
              this.logger.error('[bookingsmanager] Error sending home check-in email to'
                + ` ${user.uid}:, ${err}`);
            });
        } else if (memberMainGym.type === 'Indoor' && !isHomeBooking) {
          this.notificationService.sendStudioCheckInNotifications(user)
            .then(() => {
              this.logger.info(`sent studio check-in to ${user.uid}`);
            })
            .catch((err: unknown) => {
              this.logger.error('[bookingsmanager] Error sending studio check-in email to'
                + ` ${user.uid}:, ${err}`);
            });
        }

        user.workoutDetails.checkInNotified = time;
        await this.gymScheduler.workoutRepository.update(user.workoutDetails);
      }
    } catch (err: unknown) {
      this.logger.error('[sendCheckInNotifications] failed to send check-in notifications', err);
    }
  };

  private fetchUserOrganisation(user: UserProfile): Organisation {
    const userGym = user?.gymName
      ? DbGymRepository.getByName(user?.gymName)
      : DbGymRepository.getByName('HomeAus');
    return userGym.organisation;
  }

  private async getNextFreeSlotsForOnDemandBookingsInGymForUser(
    gym: GymInfoForTimeslots,
    userId: string,
    requestTime: Date,
    areaCategory: AreaCategory = 'circuit',
  ): Promise<ITimeSlot[]> {
    const bookingRequestTime = TimeSpan.getDateAtStartOfNextMinute(requestTime);
    const [user, schedule] = await Promise.all([
      this.getUserProfileOrThrow(userId),
      this.gymScheduler.getScheduleAroundDate(gym.name, bookingRequestTime),
    ]);

    const availableSlots: ITimeSlot[] = [];
    const workoutTypesForOnDemand = BookingsManager.getAllowedWorkoutTypesForOnDemand(gym, areaCategory);
    for (const allowedWorkoutType of workoutTypesForOnDemand) {
      const baseTargetSlots = this.buildBaseTimeSlotsForOnDemand(bookingRequestTime, allowedWorkoutType);
      const firstAvailableTargetSlot = await this.getFirstAvailableOnDemandTargetSlot(
        baseTargetSlots,
        gym,
        user,
        schedule,
        requestTime,
        areaCategory,
      );
      if (firstAvailableTargetSlot) {
        availableSlots.push(firstAvailableTargetSlot);
      }
    }

    return availableSlots;
  }

  private getSlotDisplayContent(
    timeSlot: ITimeSlot,
    gym: Gym,
  ): ITimeSlotDisplayContent {
    return {
      subText: this.getSlotSubText(timeSlot, gym),
    };
  }

  private getSlotSubText(
    timeSlot: ITimeSlot,
    gym: Gym,
  ): string | undefined {
    const showInstructorContent = gym.displayInstructorContentForTimeSlots.includes(timeSlot.timeSlotType);
    if (showInstructorContent) {
      return 'Instructor';
    }

    const descriptionForTimeSlot = gym.instructorLessTimeSlotDescription[timeSlot.timeSlotType];
    if (descriptionForTimeSlot) {
      return descriptionForTimeSlot;
    }

    return undefined;
  }

  private async getWorkoutSettingsForBooking(
    workoutSettings: WorkoutSettings | undefined,
    uid: string,
    requestedStartTime: Date,
    enabledFeatures: IEnabledFeatures,
    gym: Gym,
    user: UserProfile,
    areaCategory: AreaCategory,
  ): Promise<WorkoutSettings> {
    let settings: WorkoutSettings | undefined;
    if (workoutSettings) {
      settings = new WorkoutSettings(workoutSettings);
    } else {
      settings = await this.gymScheduler.workoutProgramRecommendations.GetRecommendedSettingsForDate(
        uid,
        requestedStartTime,
        enabledFeatures,
        gym,
        user.longTermGoal ?? ProgramType.GetStrongAndFitGoal,
        new Date(),
      );
      if (!settings) {
        settings = ProgramsConfig.fallbackSettingsForGoal(user.longTermGoal, user.skillLevel);
      }
    }
    WorkoutProgramRecommendations.forceSettingsBasedOnWorkoutArea(settings, areaCategory);

    return settings;
  }

  private getDateOfRequestForSlotCalculation(
    timeOfOpeningSlot: Date,
    dateOfRequest: Date,
    allowEvenLaterLateBookings?: boolean,
  ): Date {
    const maxLateBookingMinutes = allowEvenLaterLateBookings
      ? BookingsConfig.minutesGraceTimeForBookingsAfterClassStartedWhenEvenLaterLateBookingsEnabled
      : BookingsConfig.minutesGraceTimeForBookingsAfterClassStarted;
    const maxLateBookingMsec = maxLateBookingMinutes * 60000;

    const actualDiffSinceBookingStartTime = dateOfRequest.getTime() - timeOfOpeningSlot.getTime();

    if (actualDiffSinceBookingStartTime >= maxLateBookingMsec) {
      return dateOfRequest;
    }
    return timeOfOpeningSlot;
  }

  private async getFirstAvailableOnDemandTargetSlot(
    baseTimeSlots: IBaseTimeSlot[],
    gym: GymInfoForTimeslots,
    user: UserProfile,
    gymSchedule: IGymSchedule,
    requestTime: Date,
    areaCategory: AreaCategory,
  ): Promise<ITimeSlot | undefined> {
    const userEligibility = new UserTimeSlotTypeEligibility(user, gym);
    for (const baseTimeSlot of baseTimeSlots) {
      const timeSlot = await this.getTargetTimeSlotForOnDemandStudioBooking(
        new Date(baseTimeSlot.startTime),
        baseTimeSlot.workoutType,
        user,
        gym.name,
        gym.singleStationPerZoneGym,
        requestTime,
        areaCategory,
        gymSchedule,
      );
      if (timeSlot && !timeSlot.isFull) {
        const isAvailableForOnDemand = await this.isTimeSlotAvailableForOnDemandInGymAndForUser(
          gym,
          user,
          timeSlot,
          userEligibility,
        );
        if (isAvailableForOnDemand) {
          return timeSlot;
        }
      }
    }
    return undefined;
  }

  private async isTimeSlotAvailableForOnDemandInGymAndForUser(
    gym: GymInfoForTimeslots,
    user: UserProfile,
    timeSlot: ITimeSlot,
    userEligibility: UserTimeSlotTypeEligibility,
  ): Promise<boolean> {
    const { timeTypeAtStartOfSlot, timeTypeAtEndOfSlot } = await this.getTimeTypeRangeForWorkout(
      timeSlot.startTime,
      timeSlot.workoutType,
      user,
      gym,
    );

    const gymClosedAtStart = timeTypeAtStartOfSlot === 'Cancelled' || !timeTypeAtStartOfSlot;
    const gymClosedAtEnd = timeTypeAtEndOfSlot === 'Cancelled' || !timeTypeAtEndOfSlot;
    if (gymClosedAtStart || gymClosedAtEnd) {
      return false;
    }

    const userEligibleAtStart = userEligibility.isEligibleFor(timeTypeAtStartOfSlot);
    const userEligibleAtEnd = userEligibility.isEligibleFor(timeTypeAtEndOfSlot);
    if (!userEligibleAtStart || !userEligibleAtEnd) {
      return false;
    }

    if (gym.enableOnDemandStudioBookings === 'OffPeakOnly') {
      return timeTypeAtStartOfSlot === TimeSlotType.OffPeak && timeTypeAtEndOfSlot === TimeSlotType.OffPeak;
    }
    if (gym.enableOnDemandStudioBookings === 'Always') {
      return true;
    }

    return false;
  }

  private async getTargetTimeSlotForOnDemandStudioBooking(
    bookingTime: Date,
    workoutType: WorkoutType,
    user: UserProfile,
    gymName: string,
    withPartner: boolean,
    requestTime: Date,
    areaCategory: AreaCategory,
    gymSchedule?: IGymSchedule,
  ): Promise<ITimeSlot | undefined> {
    const gym = DbGymRepository.getByName(gymName);
    const onDemand = true;
    const userTimeTypes = new UserTimeSlotTypeEligibility(user, gym);
    const baseTimeSlot = this.gymScheduler.buildBaseTimeSlot(bookingTime.getTime(), workoutType);
    const schedule = gymSchedule || await this.gymScheduler.getScheduleAroundDate(gym.name, bookingTime);
    const requestTimeAdjustedToAllowLaterBookings = this.getDateOfRequestForSlotCalculation(
      new Date(baseTimeSlot.startTime),
      requestTime,
      false,
    );

    const zoneTypeNumber = getStaticZoneNumberFromArea(areaCategory);
    const availableSlots = await this.calculateTimeSlotForUser(
      requestTimeAdjustedToAllowLaterBookings,
      baseTimeSlot.startTime,
      withPartner,
      schedule,
      baseTimeSlot.workoutType,
      user,
      gym,
      onDemand,
      zoneTypeNumber,
    );

    const isBookingStaticCardioArea = isStaticCardioZone(areaCategory);
    return this.getTargetSlotFromAvailableSlots(
      bookingTime,
      workoutType,
      availableSlots,
      userTimeTypes,
      withPartner,
      isBookingStaticCardioArea,
    );
  }

  private getTargetSlotFromAvailableSlots(
    bookingTime: Date,
    workoutType: WorkoutType,
    availableSlots: ITimeSlot[],
    userTimeTypes: UserTimeSlotTypeEligibility,
    withPartner: boolean,
    bookingStaticCardioArea: boolean,
  ): ITimeSlot | undefined {
    return availableSlots.find((slot: ITimeSlot) => slot.workoutType === workoutType
      && userTimeTypes.isEligibleFor(slot.timeSlotType)
      && (slot.isDoubleSlotForPartner === withPartner || bookingStaticCardioArea)
      && slot.startTime === bookingTime.getTime());
  }

  private async getOpenInviteWorkoutsAndUserProfiles(workout: UserWorkoutSession, requestTime: Date) {
    const pairedId = workout.pairedSessionReference ? workout.pairedSessionReference : workout.uniqueReference;
    const pairedSessions = await this.gymScheduler.workoutRepository.getAllPairedWorkouts(pairedId, requestTime);
    const userProfileIds = pairedSessions.map((session) => session.userProfileId);
    const userProfiles = await this.gymScheduler.getUserProfileRepo().getUsersByIds(userProfileIds);
    return { pairedSessions, userProfiles };
  }

  private async delinkProvisionalPairedBookingAndExpireNow(
    workoutId: string,
    requestTime: Date,
  ): Promise<UserWorkoutSession> {
    const workout = await this.gymScheduler.getWorkoutSession(workoutId);
    if (!workout) {
      throw NotFoundError.Workout(workoutId);
    }
    const provisionalId = workout.pairedSessionReference;
    workout.pairedSessionReference = undefined;
    workout.pairedSessionUserProfileId = undefined;
    workout.pairedSession = undefined;
    await this.gymScheduler.updateWorkoutSession(workout);

    if (provisionalId) {
      const provisionalWorkout = await this.gymScheduler.getWorkoutSession(provisionalId);
      if (provisionalWorkout) {
        provisionalWorkout.expiresAt = requestTime;
        provisionalWorkout.pairedSessionReference = undefined;
        provisionalWorkout.pairedSessionUserProfileId = undefined;
        provisionalWorkout.pairedSession = undefined;
        setBookingsRemovedState(provisionalWorkout, requestTime, RemovedReasonType.Missed);
        await this.gymScheduler.updateWorkoutSession(provisionalWorkout);
      }
    }

    return workout;
  }

  private async applyInvitersWorkoutAndSettingsToInvitee(
    inviteeWorkout: UserWorkoutSession,
    inviterWorkoutReference: string,
    recommendedSettings: WorkoutSettings,
  ) {
    try {
      const inviterWorkout = await this.gymScheduler.getWorkoutSession(inviterWorkoutReference);
      if (!inviterWorkout) {
        throw new HandledError(`Paired session with reference ${inviterWorkoutReference} not found`);
      }
      inviteeWorkout.settings = inviterWorkout.settings;
      inviteeWorkout.settings.ignoreProgramRecommendations = !WorkoutProgramRecommendations
        .areSettingsTheSameForRecommendation(recommendedSettings, inviteeWorkout.settings);
      inviteeWorkout.bookingSource = BookingSourceType.App;
      const userProfileRepo = this.gymScheduler.getUserProfileRepo();
      const inviteeUserId = inviterWorkout.pairedSessionUserProfileId!;
      const invitee = await userProfileRepo.get(inviteeUserId);
      if (!(invitee instanceof UserProfile)) {
        throw new HandledError(`user not found for pairedSessionUserProfileId ${inviteeUserId}`);
      }
      await this.gymScheduler.getUserProfileRepo().update(invitee);
    } catch (error) {
      this.logger.error('Error setting invitee workout and setting to inviters (error is swallowed).', error);
    }
  }

  private async determineAvailabilityForUsersForPartner(
    users: UserProfile[],
    userId: string,
    workoutType: WorkoutType,
    workoutStartTime: Date,
    gym: GymInfoForTimeslots,
    dateOfRequest?: Date,
  ): Promise<IDisplayableUserProfile[]> {
    const user = await this.gymScheduler.getUserProfile(userId);
    if (!user) {
      throw new HandledError('Invalid userId');
    }
    if (workoutStartTime.getMinutes() === 28 || workoutStartTime.getMinutes() === 58) {
      // adjust start time to be on the half-hour/hour when it is off by 2 minutes
      // this happens when changing a partner on an existing booking (which has start time
      // of 2 mins less than requested time)
      workoutStartTime = this.gymScheduler.timeSlices.getFollowingCycle(workoutStartTime);
    }

    const workoutEndTime = new Date(workoutStartTime.getTime()
      + (workoutType * this.gymScheduler.timeSlices.sliceLengthInSeconds * 1000));
    const isCurrentBooking = (await this.gymScheduler
      .getConfirmedBookingsForUser(userId, workoutStartTime, workoutEndTime))
      .find((s: UserWorkoutSession) => Boolean(s.pairedSessionReference)
        && String(s.workoutType) === String(workoutType));
    const schedule = await this.gymScheduler.gymScheduleAt(workoutStartTime, workoutStartTime, gym, dateOfRequest);
    const results = await Promise.all(
      users.map((up: UserProfile): Promise<IDisplayableUserProfile> => this.mapToDisplayableUserProfile(
        up,
        true,
        workoutType,
        workoutStartTime,
        dateOfRequest,
        isCurrentBooking ? isCurrentBooking.pairedSessionUserProfileId : '',
        gym,
        user,
        schedule,
      )),
    );
    return results.filter((u: IDisplayableUserProfile) => u.uid !== userId);
  }

  private async preventBookingOnSameDay(
    requestTime: Date,
    startTime: Date,
    userId: string,
    allowSameDay: boolean,
    nextBookingPassedIn: UserWorkoutSession | undefined,
    gymTimezone: string,
  ) {
    if (allowSameDay) {
      return;
    }

    let nextBooking: UserWorkoutSession | undefined = nextBookingPassedIn;
    if (!nextBooking) {
      const bookingStartOfDay: Date = TimeSpan.createStartOfDayDateInTimezone(startTime, gymTimezone);
      const earliestOfNowAndStartOfDay = TimeSpan.max(requestTime, bookingStartOfDay);
      const endOfDay = TimeSpan.createEndOfDayDateInTimezone(startTime, gymTimezone);

      [nextBooking] = (await this.gymScheduler.getConfirmedBookingsForUser(
        userId,
        earliestOfNowAndStartOfDay,
        endOfDay,
        requestTime,
      ))
        .filter(WorkoutFilter.getExcludeWorkoutsDuringWarmdownFilter(requestTime));
    }
    if (!nextBooking) {
      return;
    }

    // note: this does not check for timezones across gyms in different timezones
    const gymOfNextBooking = DbGymRepository.getByName(nextBooking.gymName);
    const timezoneOfNextBooking = gymOfNextBooking.timezone;
    if (nextBooking.isSameDay(startTime, timezoneOfNextBooking)) {
      throw BookingError.notAllowed('Cannot make another booking on the same day');
    }
  }

  private subscriptionStillValidForTimeSlot(user: UserProfile, startTime: number): boolean {
    return user.isCurrentPlanActiveAt(startTime);
  }

  private async cancelSession(
    workout: UserWorkoutSession,
    dateOfRequest: Date = new Date(),
    sessionsFromNow = 0,
    removedReason?: RemovedReasonType,
  ): Promise<boolean> {
    return this.gymScheduler.cancelSession(workout, dateOfRequest, sessionsFromNow, removedReason);
  }

  private async cancelBooking(
    userId: string,
    workoutSession: UserWorkoutSession,
    dateOfRequest: Date,
    sessionsFromNow: number,
    removedReason: RemovedReasonType,
  ): Promise<void> {
    const workoutId = workoutSession.uniqueReference;
    if (workoutSession.userProfileId !== userId) {
      throw new HandledError('Workout does not belong to this user');
    }

    const gym = DbGymRepository.getByName(workoutSession.gymName);
    if (gym.type === GymTypes.Home) {
      await this.homeBookingManager.CancelBooking(workoutSession, dateOfRequest, removedReason);
      return;
    }

    if (workoutSession.pairedSessionReference) {
      let notify = false;
      try {
        await this.CancelInvite(userId, workoutId, dateOfRequest, sessionsFromNow);
      } catch (err) {
        if (err.message !== 'Specified workout is not a ProvisionalBooking or BookingInvite') {
          throw err;
        } else {
          notify = true;
        }
      }
      if (workoutSession.pairedSessionUserProfileId !== userId && this.notificationService && notify) {
        const details = {
          inviterUserId: userId,
          inviteeUserId: workoutSession.pairedSessionUserProfileId!,
          bookingReference: workoutSession.pairedSessionReference!,
          gymTimezone: gym.timezone,
        };
        this.handleNotificationResultInBackground(this.notificationService.partnerCancelled(details), details);
      }
    }
    await this.cancelSession(workoutSession, dateOfRequest, sessionsFromNow, removedReason);
  }

  private async schedule(
    left: UserWorkoutSession,
    right?: UserWorkoutSession,
    notify = false,
    dateOfRequest: Date = new Date(),
    schedule?: IGymSchedule,
    targetSlot?: ITimeSlot,
    perferredStationIndex?: number,
    workoutBookingSource?: BookingSourceType,
    staticZoneType?: number,
  ): Promise<boolean> {
    let result = false;
    if (targetSlot) {
      left.numberOfOpenSlotsAtTimeOfBooking = targetSlot.amountOfOpenSlots;
      left.isPeak = targetSlot.timeSlotType === TimeSlotType.Peak;
    }
    if (workoutBookingSource) {
      left.bookingSource = workoutBookingSource;
    }
    if (!right) {
      result = await this.gymScheduler.ScheduleSessionNow(
        left,
        perferredStationIndex,
        dateOfRequest,
        schedule,
        staticZoneType,
      );
    } else {
      if (targetSlot) {
        right.numberOfOpenSlotsAtTimeOfBooking = targetSlot.amountOfOpenSlots;
        right.isPeak = targetSlot.timeSlotType === TimeSlotType.Peak;
      }
      if (workoutBookingSource) {
        right.bookingSource = workoutBookingSource;
      }
      result = await this.gymScheduler.SchedulePairSessionNow(
        left,
        right,
        dateOfRequest,
        schedule,
        perferredStationIndex,
        staticZoneType,
      );
    }

    const gym = DbGymRepository.getByName(left.gymName);

    if (!UserWorkoutSession.isHomeBooking(left, DbGymRepository.getByName)) {
      await this.handleWaitlistAfterBooking(left, notify, dateOfRequest, right, gym);
    }

    return result;
  }

  private async handleWaitlistAfterBooking(
    left: UserWorkoutSession,
    notify: boolean,
    dateOfRequest: Date,
    right: UserWorkoutSession | undefined,
    gym: Gym,
  ) {
    const targetSlot = await this.getTargetTimeSlot(
      (await this.gymScheduler.getUserProfile(left.userProfileId))!,
      !!left.pairedSessionReference,
      left.workoutType,
      left.startTimeOfFirstExercise,
      dateOfRequest,
      gym,
    );
    if (targetSlot) {
      const leftUserProfile = await this.getUserFromUserOrUserIdOrThrowNotFoundError(left.userProfileId);
      const slots = await this.GetAvailableBookingSlots(
        Boolean(right),
        leftUserProfile,
        gym,
        TimeSpan.createStartOfDayDate(new Date(targetSlot.startTime)),
        undefined,
        dateOfRequest,
        false,
      );
      await this.removeAllFromWaitlistIfPresent(leftUserProfile, slots, gym);
      if (right) {
        const rightUserProfile = await this.getUserFromUserOrUserIdOrThrowNotFoundError(right.userProfileId);
        await this.removeAllFromWaitlistIfPresent(rightUserProfile, slots, gym);
      }
    }
  }

  private async removeAllFromWaitlistIfPresent(
    userProfile: UserProfile,
    timeSlots: ITimeSlot[],
    gym: Gym,
  ): Promise<WaitlistEntry[] | undefined> {
    if (!this.waitlist) {
      return undefined;
    }
    return this.waitlist.removeAllInRange(userProfile, timeSlots, gym.name);
  }

  private async mapToDisplayableUserProfile(
    user: UserProfile,
    workoutHasPartner: boolean,
    workoutType: WorkoutType,
    workoutStartTime: Date,
    dateOfRequest: Date = new Date(),
    currentBookingPartnerUid = '',
    gym: GymInfoForTimeslots,
    queryingUser?: UserProfile,
    schedule?: IGymSchedule,
  ): Promise<IDisplayableUserProfile> {
    const profile = DisplayableUserProfile.AsDisplayableUserProfile(user);
    const privateProfile = await this.partnersManager.isPrivate(user, queryingUser ? queryingUser.uid : undefined);
    const sessions = await this.gymScheduler.getConfirmedBookingsForUser(
      user.uid,
      dateOfRequest,
      undefined,
      dateOfRequest,
    );
    profile.eligable = await this.isUserEligibleForProvisionalBooking(
      user,
      workoutHasPartner,
      workoutType,
      workoutStartTime,
      dateOfRequest,
      gym,
      sessions,
      schedule,
    );
    profile.available = currentBookingPartnerUid !== profile.uid
      && (queryingUser ? !(await this.partnersManager.isInBlacklist(user, queryingUser)) : true)
      && await this.isAvailableForBooking(user, workoutStartTime, dateOfRequest, undefined, sessions)
      && !privateProfile;
    profile.availabilityCheckedFor = workoutStartTime;
    profile.privateProfile = privateProfile;
    return profile;
  }

  private roundToHour(date: Date, timezone?: string): Date {
    let roundedToHour = moment(date);
    if (timezone) {
      roundedToHour = roundedToHour.tz(timezone);
    }

    roundedToHour.minutes(0);
    roundedToHour.seconds(0);
    roundedToHour.milliseconds(0);
    return roundedToHour.toDate();
  }

  private async getUserFromUserOrUserId(userId: string | UserProfile): Promise<UserProfile | undefined> {
    if (!userId) {
      return undefined;
    }
    if (typeof (userId) === 'string') {
      return this.gymScheduler.getUserProfile(userId);
    }
    return userId;
  }

  private async getUserFromUserOrUserIdOrThrowNotFoundError(userId: string | UserProfile): Promise<UserProfile> {
    const user: UserProfile | undefined = await this.getUserFromUserOrUserId(userId);
    if (!user) {
      const resourceId = typeof (userId) === 'string' ? userId : '';
      throw new NotFoundError('user', resourceId);
    }
    return user;
  }

  private getLastTimeSlotForDay(timeSlots: ITimeSlot[]) {
    const sortedArray = [...timeSlots].sort((a, b) => b.startTime - a.startTime);
    return sortedArray[0];
  }

  private async mapBookedTimeSlot(
    userProfile: UserProfile,
    from: Date,
    timeSlots: ITimeSlot[],
    gym: Gym,
    dateOfRequest: Date,
    sessions: UserWorkoutSession[] | undefined,
    areaCategory: AreaCategory,
  ): Promise<ITimeSlot[]> {
    const timer = this.logger.startTimer();
    if (!sessions) {
      sessions = (await this.gymScheduler.getConfirmedBookingsForUser(userProfile.uid, from, undefined, dateOfRequest));
    }
    let returnSlots = timeSlots.map((t) => ({ ...t })); // just making a copy
    if (
      userProfile
      && areaCategory === 'circuit' // static zones do not support waitlist
      && this.waitlist
      && (await this.waitlist.getWaitinglistEntriesForUser(userProfile)).length > 0
    ) {
      returnSlots = await Promise.all(returnSlots.map((async (slot): Promise<ITimeSlot> => {
        if (this.waitlist && await this.waitlist.isInWaitingList(userProfile, slot, gym.name)) {
          slot.inWaitinglist = true;
        }
        return slot;
      })));
    }
    // there is no next session or the first next session does not fall within these timeslots
    if (sessions && sessions.length > 0) {
      sessions.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    }

    const firstUpcomingWorkout: UserWorkoutSession = sessions[0];
    const lastTimeSlotOfTheDay: ITimeSlot | undefined = this.getLastTimeSlotForDay(timeSlots);

    if (!firstUpcomingWorkout || !lastTimeSlotOfTheDay
      || firstUpcomingWorkout.startTime.getTime() > lastTimeSlotOfTheDay.startTime
    ) {
      return returnSlots;
    }
    timer.done(
      `Mapped the booked timeslots for zone-category (${areaCategory}) `
      + `and user (${userProfile.uid})`,
    );
    return returnSlots.map((timeSlot: ITimeSlot): ITimeSlot => {
      const sessionInThisTimeSlot = sessions!.find((session: UserWorkoutSession) => {
        const sessionIsInThisGym = session.gymName === gym.name;
        const startRoundedUpToTimeslot = session.startTime;
        const sessionStartsInThisSlot = (startRoundedUpToTimeslot.getTime() === timeSlot.startTime);
        const sameWorkoutType = timeSlot.workoutType === session.workoutType;
        const implicitlyOnCircuitArea = !session.areaCategory && areaCategory === 'circuit';
        const sameArea = (session.areaCategory === areaCategory) || implicitlyOnCircuitArea;
        return (sessionStartsInThisSlot && sameWorkoutType && sessionIsInThisGym && sameArea);
      });
      if (sessionInThisTimeSlot) {
        timeSlot.booked = true;
        if (areaCategory !== 'circuit' && timeSlot.isFull) {
          // this is set as unavailable below because it is full, but make it available now
          // TODO: cleanup how/where booked and isUnavailable is set
          timeSlot.isUnavailable = false;
        }
      }
      return timeSlot;
    });
  }

  private async calculateCapacity(
    roundedFrom: Date,
    to: Date,
    schedule: IGymSchedule,
    gym: GymInfoForTimeslots,
  ): Promise<ITimeslotCapacity[]> {
    let capacity: ITimeslotCapacity[] = [];
    for (let time = roundedFrom.getTime(); time <= to.getTime();
      time += BookingsConfig.bookingIntervalsInMinutes * 60 * 1000) {
      capacity = capacity.concat(await this.calculateCapacityForState(time, schedule, gym));
    }
    return capacity;
  }

  private async calculateCapacityForState(
    time: number,
    schedule: IGymSchedule,
    gym: GymInfoForTimeslots,
  ): Promise<ITimeslotCapacity[]> {
    const slots: ITimeslotCapacity[] = [];
    const state = await schedule.getState(time);
    const timeInfo = await this.gymTimeSlotsManager.getTimeSlotInfo(new Date(time), undefined, gym);
    const timeType = timeInfo.type;

    if (!timeType || timeType === 'Cancelled') {
      return slots;
    }
    for (const workoutType in WorkoutType) {
      if (!isNaN(Number(workoutType))) {
        const wt = WorkoutType[WorkoutType[workoutType]];
        slots.push({
          timeSlotType: timeType,
          workoutType: wt,
          startTime: time,
          capacity: state ? state.capacity(wt) : 0,
        });
      }
    }
    return slots;
  }

  private async calculateTimeSlotsForAnyone(
    dateOfRequest: Date,
    fromTimeRoundedToTheHour: Date,
    to: Date,
    schedule: IGymSchedule,
    gym: GymInfoForTimeslots,
    options: ICalculateTimeSlotsOptions,
  ): Promise<IBaseTimeSlotWithAvailability[]> {
    const gymTypeConfig = GymConfigs[gym.type];
    const defaultAmountOfStations = gymTypeConfig.getAmountOfStations(gym);

    const baseTimeSlots: IBaseTimeSlot[] = this.buildCircuitBaseTimeSlots(gym, fromTimeRoundedToTheHour, to);

    const timeslots: IBaseTimeSlotWithAvailability[] = [];
    for (const baseTimeSlot of baseTimeSlots) {
      const timeslot = await this.calculateTimeSlotForAnyone(
        dateOfRequest,
        baseTimeSlot,
        schedule,
        defaultAmountOfStations,
        gym,
        options,
      );
      timeslots.push(timeslot);
    }
    return timeslots;
  }

  private async calculateTimeSlotsForUser(
    timeOfRequest: Date,
    fromTimeRoundedToTheHour: Date,
    to: Date,
    withPartner: boolean,
    schedule: IGymSchedule,
    userProfile: UserProfile,
    gym: GymInfoForTimeslots,
    staticZoneType?: number,
  ): Promise<ITimeSlot[]> {
    const baseTimeSlots: IBaseTimeSlot[] = this.buildBaseTimeSlotsBasedOnArea(
      gym,
      fromTimeRoundedToTheHour,
      to,
      staticZoneType,
    );
    const onDemand = false;
    const timer = this.logger.startTimer();

    let timeslots: ITimeSlot[] = [];
    for (const baseTimeSlot of baseTimeSlots) {
      const timeslot = await this.calculateTimeSlotForUser(
        timeOfRequest,
        baseTimeSlot.startTime,
        withPartner,
        schedule,
        baseTimeSlot.workoutType,
        userProfile,
        gym,
        onDemand,
        staticZoneType,
      );
      timeslots = timeslots.concat(timeslot);
    }
    timer.done(
      `Calculated TimeSlots for zone-category (${getAreaCategory(staticZoneType)}) `
      + `and user (${userProfile.uid})`,
    );
    return timeslots;
  }

  private buildBaseTimeSlotsBasedOnArea(
    gym: GymInfoForTimeslots,
    fromTimeRoundedToTheHour: Date,
    to: Date,
    staticZoneType?: number,
  ): IBaseTimeSlot[] {
    if (isStaticCardioZone(staticZoneType)) {
      return this.buildStaticMachineBaseTimeSlots(gym, fromTimeRoundedToTheHour, to);
    }
    if (staticFunctionalAreaZones.includes(getAreaCategory(staticZoneType))) {
      return this.buildStaticFunctionalBaseTimeSlots(gym, fromTimeRoundedToTheHour, to);
    }

    return this.buildCircuitBaseTimeSlots(gym, fromTimeRoundedToTheHour, to);
  }

  private buildCircuitBaseTimeSlots(
    gym: GymInfoForTimeslots,
    fromTimeRoundedToTheHour: Date,
    to: Date,
  ): IBaseTimeSlot[] {
    const bookingTimeFrames = BookingsConfig
      .getCircuitBookingTimeFrames()
      .filter((timeFrame) => gym.allowedBookingTimeFrames.isAllowedForCircuitBookings(timeFrame));

    const allSlots = bookingTimeFrames.flatMap((timeFrame) => this.buildBaseTimeSlotsInIntervals(
      fromTimeRoundedToTheHour,
      to,
      timeFrame.bookingIntervalInMinutes,
      timeFrame.workoutType,
    ));

    return allSlots;
  }

  private buildStaticFunctionalBaseTimeSlots(
    gym: GymInfoForTimeslots,
    fromTimeRoundedToTheHour: Date,
    to: Date,
  ): IBaseTimeSlot[] {
    const bookingTimeFrames = BookingsConfig
      .getStaticFunctionalBookingTimeFrames()
      .filter((timeFrame) => gym.allowedBookingTimeFrames.isAllowedForStaticFunctionalBookings(timeFrame));

    const allSlots = bookingTimeFrames.flatMap((timeFrame) => this.buildBaseTimeSlotsInIntervals(
      fromTimeRoundedToTheHour,
      to,
      timeFrame.bookingIntervalInMinutes,
      timeFrame.workoutType,
    ));

    return allSlots;
  }

  private buildStaticMachineBaseTimeSlots(
    gym: GymInfoForTimeslots,
    fromTimeRoundedToTheHour: Date,
    to: Date,
  ): IBaseTimeSlot[] {
    const bookingTimeFrames = BookingsConfig
      .getStaticMachineBookingTimeFrames()
      .filter((timeFrame) => gym.allowedBookingTimeFrames.isAllowedForStaticMachineBookings(timeFrame));

    const allSlots = bookingTimeFrames.flatMap((timeFrame) => this.buildBaseTimeSlotsInIntervals(
      fromTimeRoundedToTheHour,
      to,
      timeFrame.bookingIntervalInMinutes,
      timeFrame.workoutType,
    ));

    return allSlots;
  }

  private buildBaseTimeSlotsInIntervals(
    fromTimeRoundedToTheHour: Date,
    to: Date,
    intervalMinutes: number,
    workoutType: number,
  ): IBaseTimeSlot[] {
    let timeslots: IBaseTimeSlot[] = [];
    for (let timeIntervalIterator = fromTimeRoundedToTheHour.getTime(); timeIntervalIterator <= to.getTime();
      timeIntervalIterator += TimeSpan.millisecondsForMinutes(intervalMinutes)) {
      const timeslot = this.gymScheduler.buildBaseTimeSlot(
        this.calculateStartTime(intervalMinutes, timeIntervalIterator),
        workoutType,
      );
      timeslots = timeslots.concat(timeslot);
    }
    return timeslots;
  }

  private calculateStartTime(intervalMinutes: number, startTime: number): number {
    const timeslotStartTime = new Date(startTime);
    const minutes = timeslotStartTime.getMinutes();
    if (minutes % 2 === 1 && intervalMinutes % 2 === 1) {
      startTime += TimeSpan.millisecondsForMinutes(1);
    }
    return startTime;
  }

  private buildBaseTimeSlotsForOnDemand(requestTime: Date, workoutType: WorkoutType): IBaseTimeSlot[] {
    const firstPossibleStartTime = this.gymScheduler.timeSlices.nextSliceClosestToTime(requestTime);
    const cutOffTimeForCheckin = moment(firstPossibleStartTime)
      .add(BookingsConfig.maxNumberOfMinutesPriorToExerciseForCheckin, 'minutes')
      .toDate();

    const baseTimeSlotsUntilEarliestCheckInTime: IBaseTimeSlot[] = [];
    for (
      let timeSliceIterator = firstPossibleStartTime;
      timeSliceIterator < cutOffTimeForCheckin;
      timeSliceIterator = this.gymScheduler.timeSlices.getFollowingCycle(timeSliceIterator)
    ) {
      baseTimeSlotsUntilEarliestCheckInTime.push(
        this.gymScheduler.buildBaseTimeSlot(timeSliceIterator.getTime(), workoutType),
      );
    }
    return baseTimeSlotsUntilEarliestCheckInTime;
  }

  private async calculateTimeSlotForAnyone(
    timeOfRequest: Date,
    baseTimeSlot: IBaseTimeSlot,
    schedule: IGymSchedule,
    defaultAmountOfStations: number,
    gym: GymInfoForTimeslots,
    options: ICalculateTimeSlotsOptions,
  ): Promise<IBaseTimeSlotWithAvailability> {
    const timeInfo = await this.gymTimeSlotsManager.getTimeSlotInfo(new Date(baseTimeSlot.startTime), undefined, gym);
    const timeType = timeInfo.type;

    function getClosedReason(): TimeSlotClosedReason | undefined {
      if (!timeType || timeType === TimeSlotType.None) {
        return 'Gym Closed';
      }
      if (timeType === 'Cancelled') {
        return 'Cancelled';
      }
      return undefined;
    }

    const closedReason = getClosedReason();

    function getAmountOfStations(): number {
      if (closedReason) {
        return 0;
      }
      return defaultAmountOfStations;
    }

    const amountOfStations = getAmountOfStations();

    const getAmountOfOpenSlots = async () => {
      if (options.onlyPeakTimeslots && timeType !== TimeSlotType.Peak) {
        return 0;
      }
      if (closedReason) {
        return 0;
      }

      return this.calculateAllowedAmountOfFreeStationsFromRoundedStartIfInFuture(
        timeOfRequest,
        baseTimeSlot.startTime,
        schedule,
        baseTimeSlot.workoutType,
        options.withPartner,
        gym,
      );
    };

    const amountOfOpenSlots = await getAmountOfOpenSlots();

    return {
      ...baseTimeSlot,
      closedReason,
      amountOfOpenSlots,
      amountOfStations,
    };
  }

  private async getTimeTypeRangeForWorkout(
    fromTimeRoundedToTheBookingInterval: number,
    workoutType: WorkoutType,
    userProfile: UserProfile,
    gym: GymInfoForTimeslots,
  ): Promise<ITimeSlotTypeRange> {
    const classDuration = TimeSpan.millisecondsForMinutes(BookingsConfig.workoutTypeDuration(workoutType));
    const smallGapBeforeStartOfNextCycle = 1;

    const [timeInfoAtStartOfSlot, timeInfoAtEndOfSlot] = await Promise.all([
      this.gymTimeSlotsManager.getTimeSlotInfo(
        new Date(fromTimeRoundedToTheBookingInterval),
        userProfile,
        gym,
      ),
      this.gymTimeSlotsManager.getTimeSlotInfo(
        new Date(fromTimeRoundedToTheBookingInterval + classDuration - smallGapBeforeStartOfNextCycle),
        userProfile,
        gym,
      ),
    ]);
    const [timeTypeAtStartOfSlot, timeTypeAtEndOfSlot] = [timeInfoAtStartOfSlot.type, timeInfoAtEndOfSlot.type];

    return {
      timeTypeAtStartOfSlot,
      timeTypeAtEndOfSlot,
    };
  }

  private async calculateTimeSlotForUser(
    timeOfRequest: Date,
    fromTimeRoundedToTheBookingInterval: number,
    withPartner: boolean,
    schedule: IGymSchedule,
    workoutType: WorkoutType,
    userProfile: UserProfile,
    gym: GymInfoForTimeslots,
    onDemand: boolean,
    staticZoneType?: number,
  ): Promise<ITimeSlot[]> {
    const timeslots: ITimeSlot[] = [];
    const areaCategory = getAreaCategory(staticZoneType);
    const amountOfStations = getAmountOfStationsForCategory(gym.zoneMapping, areaCategory);

    const { timeTypeAtStartOfSlot, timeTypeAtEndOfSlot } = await this.getTimeTypeRangeForWorkout(
      fromTimeRoundedToTheBookingInterval,
      workoutType,
      userProfile,
      gym,
    );

    // Todo: Cancelled or undefined means that the gym is closed. would be nice to have a closed timeType.
    //  could be included in new getTimeTypeRangeForWorkout function
    const gymUnavailableAtEitherStartOrEndOfSlot = !timeTypeAtStartOfSlot
      || !timeTypeAtEndOfSlot
      || timeTypeAtStartOfSlot === 'Cancelled'
      || timeTypeAtEndOfSlot === 'Cancelled';
    if (gymUnavailableAtEitherStartOrEndOfSlot) {
      return timeslots;
    }
    const allowedAmountOfFreeStations = await this.calculateAllowedAmountOfFreeStationsFromRoundedStartIfInFuture(
      timeOfRequest,
      fromTimeRoundedToTheBookingInterval,
      schedule,
      workoutType,
      withPartner,
      gym,
      staticZoneType,
    );

    const timeTypeForSlot = this.determineTimeSlotTypeForUser(
      timeTypeAtStartOfSlot,
      timeTypeAtEndOfSlot,
      userProfile,
    );
    // Todo: Kept to keep the class pass integration working.
    const allowedAmountOfFreeStationsForTimeSlot = timeTypeForSlot === TimeSlotType.None
      ? 0 : allowedAmountOfFreeStations;

    timeslots.push(this.createTimeSlot(
      amountOfStations,
      timeTypeForSlot,
      workoutType,
      withPartner,
      fromTimeRoundedToTheBookingInterval,
      allowedAmountOfFreeStationsForTimeSlot,
      staticZoneType !== undefined,
      onDemand,
    ));
    return timeslots;
  }

  private determineTimeSlotTypeForUser(
    timeTypeAtStartOfSlot: TimeSlotType,
    timeTypeAtEndOfSlot: TimeSlotType,
    user: UserProfile,
  ): TimeSlotType {
    const doesSlotSpanMultiTimeTypes = timeTypeAtStartOfSlot !== timeTypeAtEndOfSlot;

    if (doesSlotSpanMultiTimeTypes) {
      const isAnyTimeUser = user.timeSlotType === TimeSlotType.AnyTime;
      // only need to check if the user is eligible to book AnyTime
      // the gym's eligibility is checked in isTimeSlotAvailableForOnDemandInGymAndForUser
      if (isAnyTimeUser) {
        return timeTypeAtEndOfSlot;
      }
      return TimeSlotType.None;
    }
    return timeTypeAtEndOfSlot;
  }

  private async calculateAllowedAmountOfFreeStationsFromRoundedStartIfInFuture(
    timeOfRequest: Date,
    fromTimeRoundedToTheBookingInterval: number,
    schedule: IGymSchedule,
    workoutType: WorkoutType,
    withPartner: boolean,
    gym: Gym,
    staticZoneType?: number,
  ): Promise<number> {
    const isRequestingForSlotInPast = fromTimeRoundedToTheBookingInterval < timeOfRequest.getTime();
    if (isRequestingForSlotInPast) {
      return 0;
    }

    const allowedAmountOfFreeStations: number = await this.allowedAmountOfFreeStations(
      schedule,
      new Date(fromTimeRoundedToTheBookingInterval),
      workoutType,
      withPartner,
      gym,
      staticZoneType,
    );
    return allowedAmountOfFreeStations;
  }

  private async allowedAmountOfFreeStations(
    schedule: IGymSchedule,
    bookingTime: Date,
    workoutType: WorkoutType,
    findFullZones: boolean,
    gym: Gym,
    staticZoneType?: number,
  ) {
    const gymTypeConfig = GymConfigs[gym.type];
    const totalAmountOfStationsInGymForAllCategories = gymTypeConfig.getAmountOfStations(gym);

    const cycleTimeUsedInCalculations = bookingTime;
    const freeStations = await this.gymScheduler.findFreeStations(
      schedule,
      gym,
      cycleTimeUsedInCalculations,
      workoutType,
      totalAmountOfStationsInGymForAllCategories,
      findFullZones,
      gym.usersWillExerciseInNextZoneAfterPause,
      staticZoneType,
    );

    const totalAmountOfFreeStations = freeStations.length;
    let allowedAmountOfFreeStations: number;
    if (gym.allowedCapacityAsPercentOfFullGym < 0 || gym.allowedCapacityAsPercentOfFullGym > 1) {
      throw Error(
        `Gym with name ${gym.name} has an invalid allowed capacity of ${gym.allowedCapacityAsPercentOfFullGym}`,
      );
    } else if (gym.allowedCapacityAsPercentOfFullGym === 1) {
      allowedAmountOfFreeStations = totalAmountOfFreeStations;
    } else {
      allowedAmountOfFreeStations = Math.floor(totalAmountOfFreeStations * gym.allowedCapacityAsPercentOfFullGym);
    }

    return allowedAmountOfFreeStations;
  }

  private createTimeSlot(
    amountOfStations: number,
    timeSlotType: TimeSlotType,
    workoutType: WorkoutType,
    withPartner: boolean,
    startTime: number,
    amountOfOpenSlots: number,
    isStaticZoneSlot: boolean,
    isOnDemand: boolean,
  ): ITimeSlot {
    const base = this.gymScheduler.buildBaseTimeSlot(startTime, workoutType);
    const isFull = amountOfOpenSlots === 0;

    function calculateIsUnavailable() {
      if (isFull && isStaticZoneSlot) {
        // dont allow waitlist bookings for this (hidden in front end)
        return true;
      }
      return false;
    }

    return {
      ...base,
      timeSlotType,
      isDoubleSlotForPartner: withPartner,
      isFull,
      amountOfOpenSlots,
      booked: false,
      inWaitinglist: false,
      isUnavailable: calculateIsUnavailable(),
      amountOfStations,
      bookingType: isOnDemand ? 'instant' : 'scheduled',
    };
  }

  private async checkInSessionForGym(
    gym: Gym,
    session: UserWorkoutSession,
    checkInTime: Date,
    skippedBiometric: boolean,
  ): Promise<void> {
    if (gym.autoCheckin) {
      await this.setSessionAsCheckedIn(session, checkInTime, skippedBiometric, gym);
    }
  }

  private async setSessionAsCheckedIn(
    session: UserWorkoutSession,
    checkInTime: Date,
    skippedBiometric: boolean,
    gym: Gym,
  ): Promise<void> {
    if (gym.type !== GymTypes.Home && this.marketingAnalyticsService) {
      this.gymScheduler.getUserProfile(session.userProfileId).then(
        (userProfile) => userProfile
          && this.marketingAnalyticsService?.logEventInBackground('studio_workout_checkin', userProfile),
      ).catch((e) => this.logger.warn('failed to load user for marketingAnalyticsService', e));
    }
    const workoutSelectionId = await this.gymScheduler.getWorkoutSelectionIdForSettings(
      session.settings,
      gym,
      session.userProfileId,
      session.areaCategory,
    );

    UserWorkoutSession.markSessionAsCheckedIn(session, skippedBiometric, checkInTime, workoutSelectionId);
  }

  private async markSessionAsCheckedIn(
    session: UserWorkoutSession,
    checkInTime: Date,
    skippedBiometric: boolean,
    gym: Gym,
  ): Promise<void> {
    await this.setSessionAsCheckedIn(session, checkInTime, skippedBiometric, gym);
    return this.gymScheduler.updateWorkoutSession(session);
  }

  private async isUserEligibleForProvisionalBooking(
    user: UserProfile,
    withPartner: boolean,
    workoutType: WorkoutType,
    startTime: Date,
    dateOfRequest: Date,
    gym: GymInfoForTimeslots,
    sessions?: UserWorkoutSession[],
    schedule?: IGymSchedule,
  ): Promise<boolean> {
    const userTimeTypes = new UserTimeSlotTypeEligibility(user, gym);
    const timeInfo = await this.gymTimeSlotsManager.getTimeSlotInfo(startTime, user, gym);
    const timeType = timeInfo.type;

    if (!timeType || timeType === 'Cancelled') {
      return false;
    }
    if (!userTimeTypes.isEligibleFor(timeType)) {
      return false;
    }
    return this.subscriptionStillValidForTimeSlot(user, startTime.getTime());
  }

  private async rejectAllReceivedInvitesForSpecifiedDay(
    userId: string,
    from: Date = new Date(),
    dateOfRequest: Date = new Date(),
  ): Promise<void[]> {
    const startOfDay = TimeSpan.createStartOfDayDate(from);
    const to = TimeSpan.createEndOfDayDate(startOfDay);
    const sessions = await this.gymScheduler.getInvitesForUser(userId, startOfDay, to);
    return Promise.all(
      sessions.map(
        async (session: UserWorkoutSession) => this.RejectInvite(userId, session.uniqueReference, dateOfRequest),
      ),
    );
  }

  private createProvisionalBooking(
    userId: string,
    workoutType: WorkoutType,
    workoutSettings: WorkoutSettings,
    startTime: Date,
    gym: Gym,
    dateOfRequest: Date = new Date(),
    userGymName?: string,
    staticZoneNumber?: number,
  ): UserWorkoutSession {
    const newUserWorkoutSession = new UserWorkoutSession(
      userId,
      workoutType,
      workoutSettings,
      undefined,
      new Date(startTime.getTime()),
      BookingState.ProvisionalBooking,
      dateOfRequest,
      gym,
      undefined,
      undefined,
      staticZoneNumber,
    );
    newUserWorkoutSession.memberMainGym = userGymName;
    return newUserWorkoutSession;
  }

  private handleNotificationResultInBackground(
    pendingNotification: Promise<INotifyResults>,
    invitationDetails: IInvitationDetails,
  ) {
    this.handleNotificationResult(pendingNotification, invitationDetails)
      .catch((e) => this.logger.warn('handleNotificationResult failed', e));
  }

  private async handleNotificationResult(
    pendingNotification: Promise<INotifyResults>,
    invitationDetails: IInvitationDetails,
  ): Promise<INotifyResults | undefined> {
    let notificationResults;
    try {
      notificationResults = await pendingNotification;
      this.logger.info('notified', invitationDetails, notificationResults);
    } catch (err) {
      this.logger.error('error notifying', invitationDetails, err);
    }
    return notificationResults;
  }

  private async getPerferredStationForSessionsOnHalfHour(
    workoutSession: UserWorkoutSession,
    amountOfStations: number,
  ): Promise<number> {
    if (this.classStartOnHalfHour(workoutSession)) {
      return this.gymScheduler.getStartingStationForHalfHourSessions(amountOfStations);
    }

    return BookingsConfig.defaultStartingStation;
  }

  private classStartOnHalfHour(workoutSession: UserWorkoutSession): boolean {
    const earliestStartingTimeInMinutes = new Date(workoutSession.earliestAllowedStartTime).getMinutes();
    return workoutSession.workoutType === WorkoutType.MediumStudioWorkout
      && earliestStartingTimeInMinutes === BookingsConfig.earliestStartingMinuteForBookingsOnHalfHour;
  }

  private groupTimeSlotsByWorkoutType(timeSlotViewModels: ITimeSlotViewModel[])
    : { [key: string]: ITimeSlotViewModel[] } {
    return timeSlotViewModels.reduce((acc, timeSlot) => {
      const workoutType = timeSlot.workoutType;
      if (!acc[workoutType]) {
        acc[workoutType] = [];
      }
      acc[workoutType].push(timeSlot);
      return acc;
    }, {} as { [key: string]: ITimeSlotViewModel[] });
  }

  private transformAndFilterGroupedTimeSlots(
    areaCategory: AreaCategory,
    requestTime: Date,
    groupedByWorkoutType: { [key: string]: ITimeSlotViewModel[] },
  ): IWorkoutTypeClassTimeData[] {
    const minutesAfterStart = areaCategory === 'circuit'
      ? BookingsConfig.minutesGraceTimeForBookingsAfterClassStarted
      : BookingsConfig.minutesGraceTimeForStaticBookingsAfterClassStarted;
    const miliSecondsAfterStart = minutesAfterStart * 60 * 1000;
    const earliestAllowedStartTime = requestTime.getTime() - miliSecondsAfterStart;

    function includeSlotInResult(slot: ITimeSlotViewModel): boolean {
      if (slot.booked && slot.endTime > requestTime.getTime()) {
        return true;
      }
      if (slot.startTime <= earliestAllowedStartTime) {
        return false;
      }
      if (slot.timeSlotType === TimeSlotType.None) {
        return false;
      }

      return true;
    }

    return Object.entries(groupedByWorkoutType).map(([workoutType, classTimes]) => ({
      workoutType: Number(workoutType) as WorkoutType,
      classTimes: classTimes.filter(includeSlotInResult),
    }));
  }

  private async getUserProfileOrThrow(userId: string | UserProfile): Promise<UserProfile> {
    let user;
    if (typeof (userId) === 'string') {
      user = await this.gymScheduler.getUserProfile(userId);
    } else {
      user = userId;
    }
    if (!user) {
      const resourceId = typeof (userId) === 'string' ? userId : '';
      throw new NotFoundError('user', resourceId);
    }
    return user;
  }

  private calculateDateToUseForSlots(
    from: Date,
    dateOfRequest: Date,
    gym: Gym,
  ): Date {
    const fromInGymTimezone = moment(from).tz(gym.timezone);
    const dateOfRequestInGymTimezone = moment(dateOfRequest).tz(gym.timezone);
    const isFromAndRequestSameDay = fromInGymTimezone.isSame(dateOfRequestInGymTimezone, 'day');
    this.logger.info('GetAvailableBookingSlotsForGym.calculateDateToUseForSlots', {
      gymTimezone: gym.timezone,
      fromInGymTimezone: fromInGymTimezone.format(),
      dateOfRequestInGymTimezone: dateOfRequestInGymTimezone.format(),
    });

    if (isFromAndRequestSameDay) {
      const fromTimeRoundedToTheHalfHour = this.roundToHour(dateOfRequest);
      const isRequestedOnHalfHourSlotsForAvailability = dateOfRequest.getMinutes() >= 30;
      if (isRequestedOnHalfHourSlotsForAvailability) {
        fromTimeRoundedToTheHalfHour.setMinutes(30);
      }

      this.logger.info('using calculated fromTime', {
        from,
        dateOfRequest,
        isFromAndRequestSameDay,
        isRequestedOnHalfHourSlotsForAvailability,
        fromToUse: fromTimeRoundedToTheHalfHour,
      });
      return fromTimeRoundedToTheHalfHour;
    }

    this.logger.info('using passed in "from" date', {
      from,
      dateOfRequest,
      isFromAndRequestSameDay,
    });
    return from;
  }
}
